import { useMemo } from 'react';
import {
  Person,
  Relationship,
  LayoutNode,
  LayoutLink,
  TreeViewMode,
  SpouseInfo,
  GenealogyEvent,
} from '../types.ts';
import type { CardLevel } from '../types.ts';
import { getMarriageInfo } from '../utils/relationshipEvents.ts';
import { getFeatureFlag } from '../utils/featureFlags.ts';
import { resolvePediType, isParentRel } from '../utils/pediType.ts';
import { resolvePartnerLineType } from '../utils/partnerLineType.ts';
import { useTreeLayoutV2 } from './useTreeLayoutV2.ts';
import { plaatsGezinslijnen, verwijsKaartId } from './treeGezinslijnen.ts';
import { applyScreenPartnerSpacing } from './treeScreenPartnerSpacing.ts';
import { MIN_SIBLING_GAP_REG, computeCardWidthForLevel } from '../utils/treePdfShared.ts';
import { assignDabovilleNumbers, type DabovilleNode } from '../utils/dabovilleUtils.ts';

// ─── Export-redesign (#525) ─────────────────────────────────────────────────
/**
 * Wijs een card-density niveau toe op basis van de generatie-afstand tot
 * de proband. Volgt spec 2 § 3.4:
 *   |gen| ≤ 3 → detailed   (proband + dichtbij)
 *   |gen| ≤ 5 → standard   (middenring)
 *   |gen| ≥ 6 → compact    (verre takken, jaar-bereik weergave)
 *
 * Werkt voor zowel ancestors (positieve generation) als descendants (negatief);
 * absolute waarde zorgt dat beide richtingen hetzelfde niveau krijgen.
 */
export function assignCardLevel(generationDistance: number): CardLevel {
  const absDist = Math.abs(generationDistance);
  // #525 post-Plan-A: proband + ouders = detailed (220), rest = standard (180).
  // Outer-ring krijgt rotated via __rotated flag (bypasst dispatcher).
  if (absDist <= 1) return 'detailed';
  return 'standard';
}

// ─── Layout Constants ────────────────────────────────────────────────────────
export const NODE_WIDTH = 230;
export const NODE_HEIGHT = 195;
export const H_GAP = 50; // horizontal gap between siblings
export const V_GAP = 40; // vertical gap between generations
export const SPOUSE_GAP = 10; // gap between spouses (tighter pairing)
const PARTNER_GROUP_GAP = 30; // extra gap between partner groups in descendants view
/** Hoe ver de inklapknop van de partners rechts uit de kaart steekt (TreeNode `right`). */
export const INKLAPKNOP_UITSTEEK = 14;
/** Het hart op de huwelijkslijn (TreeLinks, fontSize 10), gemeten 7 px breed. */
const HART_BREEDTE = 7;
/** Vrije ruimte tussen knop en hart. */
const KNOP_MARGE = 4;
/**
 * #1606 ronde 2 — extra ruimte op het SCHERM tussen een vrouw en de man rechts naast haar
 * na de partnerwissel (DESCENDANTS). Daar staan haar inklapknop (24 breed, steekt
 * INKLAPKNOP_UITSTEEK uit de kaart), het hart en "(2)" (10 breed), beide gecentreerd in de
 * opening. Gemeten op het scherm (1606r2-meting, zoom 1): opening 10, knop 1315–1339
 * (4 px over Gerrits kaart), hart 1326,6–1333,6 (onder de knop).
 * Vrij van de knop: halve opening ≥ 14 + 4 + 7/2 = 21,5 → opening 43 = SPOUSE_GAP 10 + 33.
 */
export const SCHERM_WISSEL_EXTRA =
  2 * (INKLAPKNOP_UITSTEEK + KNOP_MARGE + HART_BREEDTE / 2) - SPOUSE_GAP;

// ─── Compact Mode (Generation 4+) ───────────────────────────────────────────
export const COMPACT_GEN = 4;
export const COMPACT_NODE_WIDTH = 111;
// #561 — 160 → 172: ruimte voor de gestapelde volledige-datum-variant op compact
// cards (gen4+) bij fullDates AAN, zodat plaats/beroep zichtbaar blijven (#1038).
export const COMPACT_NODE_HEIGHT = 172;
export const COMPACT_H_GAP = 10;
export const COMPACT_SPOUSE_GAP = 8;
/** #1590 — verticale afstand tussen de gezinsbalken van opeenvolgende huwelijken
 * (negatief: elk volgend huwelijk iets hóger, zodat de lijnen naar de kinderen lang
 * genoeg blijven voor het streeppatroon van adoptie e.d.). */
const GEZINSBALK_STAP = -12;
/** #1590 — hoeveel van de opening (V_GAP 40) alle gezinsbalken samen mogen gebruiken. */
const GEZINSBALK_RUIMTE = 16;

// compactThreshold can be overridden per layout pass (Infinity = never compact)
let _compactGen = COMPACT_GEN;

/** Get node dimensions based on generation depth. */
function getNodeDims(generation: number) {
  const compact = Math.abs(generation) >= _compactGen;
  return {
    width: compact ? COMPACT_NODE_WIDTH : NODE_WIDTH,
    hGap: compact ? COMPACT_H_GAP : H_GAP,
    spouseGap: compact ? COMPACT_SPOUSE_GAP : SPOUSE_GAP,
  };
}

/** Get the node height for a given generation. */
function getNodeHeight(generation: number): number {
  return Math.abs(generation) >= _compactGen ? COMPACT_NODE_HEIGHT : NODE_HEIGHT;
}

/** Compute the Y offset for a generation, accumulating mixed heights per step.
 *  Ancestors (positive gen) → negative Y (upward). Descendants (negative gen) → positive Y. */
function getGenY(generation: number): number {
  const absGen = Math.abs(generation);
  let y = 0;
  for (let g = 0; g < absGen; g++) {
    const h = g >= _compactGen ? COMPACT_NODE_HEIGHT : NODE_HEIGHT;
    y += h + V_GAP;
  }
  return generation >= 0 ? -y : y;
}

// ─── Relationship Index ──────────────────────────────────────────────────────
interface RelIndex {
  byChild: Map<string, Relationship[]>; // child → parent-child relationships
  byParent: Map<string, Relationship[]>; // parent → parent-child relationships
  spousal: Map<string, Relationship[]>; // person → spousal relationships
}

// ─── Internal subtree result ─────────────────────────────────────────────────
interface SubtreeResult {
  width: number;
  nodes: LayoutNode[];
  links: LayoutLink[];
}

/**
 * Build a fast lookup index from the flat relationships array.
 * In parent-child relationships, personA = parent, personB = child.
 */
export function buildRelIndex(relationships: Relationship[]): RelIndex {
  const byChild = new Map<string, Relationship[]>();
  const byParent = new Map<string, Relationship[]>();
  const spousal = new Map<string, Relationship[]>();

  for (const rel of relationships) {
    if (isParentRel(rel)) {
      // personA = parent, personB = child
      const childRels = byChild.get(rel.personB) ?? [];
      childRels.push(rel);
      byChild.set(rel.personB, childRels);

      const parentRels = byParent.get(rel.personA) ?? [];
      parentRels.push(rel);
      byParent.set(rel.personA, parentRels);
    } else if (rel.type === 'spousal') {
      // Index both directions
      const relsA = spousal.get(rel.personA) ?? [];
      relsA.push(rel);
      spousal.set(rel.personA, relsA);

      const relsB = spousal.get(rel.personB) ?? [];
      relsB.push(rel);
      spousal.set(rel.personB, relsB);
    }
  }

  return { byChild, byParent, spousal };
}

/**
 * Get filtered parents for a person based on view mode.
 * Returns [father, mother] order for FULL, single parent for lineage modes.
 */
function getFilteredParents(
  personId: string,
  relIndex: RelIndex,
  personMap: Map<string, Person>,
  viewMode: TreeViewMode
): Person[] {
  const parentRels = relIndex.byChild.get(personId) ?? [];
  const parents: Person[] = [];

  for (const rel of parentRels) {
    const parent = personMap.get(rel.personA);
    if (parent) parents.push(parent);
  }

  if (parents.length === 0) return [];

  switch (viewMode) {
    case 'PATRILINEAL':
      return parents.filter((p) => p.gender === 'M').slice(0, 1);
    case 'MATRILINEAL':
      return parents.filter((p) => p.gender === 'F').slice(0, 1);
    case 'PEDIGREE':
    default: {
      // Sort: father (M) first, then mother (F), then others
      const sorted: Person[] = [];
      const father = parents.find((p) => p.gender === 'M');
      const mother = parents.find((p) => p.gender === 'F');
      if (father) sorted.push(father);
      if (mother) sorted.push(mother);
      // Add any remaining parents not yet included
      for (const p of parents) {
        if (!sorted.includes(p)) sorted.push(p);
      }
      return sorted.slice(0, 2); // Max 2 parents
    }
  }
}

/**
 * Get children for a person (used in DESCENDANTS mode).
 */
function getChildren(
  personId: string,
  relIndex: RelIndex,
  personMap: Map<string, Person>
): Person[] {
  const childRels = relIndex.byParent.get(personId) ?? [];
  const children: Person[] = [];
  const seen = new Set<string>();

  for (const rel of childRels) {
    if (seen.has(rel.personB)) continue;
    seen.add(rel.personB);
    const child = personMap.get(rel.personB);
    if (child) children.push(child);
  }

  // Sort children by birth year (unknown dates last)
  children.sort((a, b) => {
    const ya = a.birthDate?.match(/\d{4}/)?.[0];
    const yb = b.birthDate?.match(/\d{4}/)?.[0];
    return (ya ? parseInt(ya) : 9999) - (yb ? parseInt(yb) : 9999);
  });

  return children;
}

/**
 * Get spouses for a person.
 */
function getSpouses(
  personId: string,
  relIndex: RelIndex,
  personMap: Map<string, Person>
): Person[] {
  const spouseRels = relIndex.spousal.get(personId) ?? [];
  const spouses: Person[] = [];
  const seen = new Set<string>();

  for (const rel of spouseRels) {
    const spouseId = rel.personA === personId ? rel.personB : rel.personA;
    if (seen.has(spouseId)) continue;
    seen.add(spouseId);
    const spouse = personMap.get(spouseId);
    if (spouse) spouses.push(spouse);
  }

  return spouses;
}

/**
 * Resolve pediType for a parent→child pair.
 * Only populated when the `relationLines` flag is on; returns undefined otherwise
 * (byte-identical to the old behaviour when the flag is off).
 */
function pediTypeFor(
  parentId: string,
  childId: string,
  relIndex: RelIndex,
  on: boolean
): LayoutLink['pediType'] | undefined {
  if (!on) return undefined;
  const rel = (relIndex.byChild.get(childId) ?? []).find((r) => r.personA === parentId);
  if (!rel) return undefined;
  // relation_subtype is de bron-van-waarheid; val terug op het coarse type voor legacy-data.
  return resolvePediType(rel.relationSubtype ?? rel.type);
}

// ─── Partner-Aware Helpers ──────────────────────────────────────────────────

interface SpouseSortedEntry {
  spouse: Person;
  status: 'active' | 'divorced' | 'deceased';
  marriageDate?: string;
  marriagePlace?: string;
  rel: Relationship;
}

/**
 * Get spouses sorted by marriage date (chronological, oldest first).
 * Also determines relationship status: active, divorced, or deceased.
 *
 * #525 — gebruikt nu `getMarriageInfo` om de echte marriage-event data
 * (datum + plaats) op te halen uit de events-array.
 */
function getSpousesSorted(
  personId: string,
  relIndex: RelIndex,
  personMap: Map<string, Person>,
  events: GenealogyEvent[]
): SpouseSortedEntry[] {
  const spouseRels = relIndex.spousal.get(personId) ?? [];
  const result: SpouseSortedEntry[] = [];
  const seen = new Set<string>();

  for (const rel of spouseRels) {
    const spouseId = rel.personA === personId ? rel.personB : rel.personA;
    if (seen.has(spouseId)) continue;
    seen.add(spouseId);

    const spouse = personMap.get(spouseId);
    if (!spouse) continue;

    const marriageInfo = getMarriageInfo(rel, events);
    const divorceEvent = events.find(
      (e) =>
        e.type === 'divorce' &&
        e.participants?.some((p) => p.personId === rel.personA) &&
        e.participants?.some((p) => p.personId === rel.personB)
    );

    let status: 'active' | 'divorced' | 'deceased' = 'active';
    if (divorceEvent) status = 'divorced';
    else if (spouse.deathDate && !spouse.isLiving) status = 'deceased';

    result.push({
      spouse,
      status,
      marriageDate: marriageInfo.date,
      marriagePlace: marriageInfo.place,
      rel,
    });
  }

  // #590 — leid partners óók af uit marriage-events. Een paar kan een huwelijk
  // hebben (beide als partner/subject op het event) zónder expliciete spousal-
  // relatie-rij (bv. alleen verbonden als co-ouders) — dan miste de banner, omdat
  // de spouse-lijst tot nu toe puur uit `relIndex.spousal` kwam.
  const SPOUSE_PARTICIPANT_ROLES = new Set(['partner', 'subject']);
  for (const ev of events) {
    if (ev.type !== 'marriage') continue;
    const partnerIds = (ev.participants ?? [])
      .filter((p) => p.personId && SPOUSE_PARTICIPANT_ROLES.has(p.role as string))
      .map((p) => p.personId as string);
    if (!partnerIds.includes(personId)) continue;
    for (const otherId of partnerIds) {
      if (otherId === personId || seen.has(otherId)) continue;
      const spouse = personMap.get(otherId);
      if (!spouse) continue;
      seen.add(otherId);
      const synthRel: Relationship = {
        id: `synth-marriage-${ev.id}-${otherId}`,
        projectId: '',
        personA: personId,
        personB: otherId,
        type: 'spousal',
      };
      const marriageInfo = getMarriageInfo(synthRel, events);
      const divorceEvent = events.find(
        (e) =>
          e.type === 'divorce' &&
          e.participants?.some((p) => p.personId === personId) &&
          e.participants?.some((p) => p.personId === otherId)
      );
      let status: 'active' | 'divorced' | 'deceased' = 'active';
      if (divorceEvent) status = 'divorced';
      else if (spouse.deathDate && !spouse.isLiving) status = 'deceased';
      result.push({
        spouse,
        status,
        marriageDate: marriageInfo.date,
        marriagePlace: marriageInfo.place,
        rel: synthRel,
      });
    }
  }

  // #1793 (review Twan) — zonder huwelijksdatum besliste de toevallige volgorde
  // van de relaties wie de "eerste" partner was, en daarmee de kant (1e rechts,
  // 2e links). Terugval: het oudste gezamenlijke kind (als beide paren er hebben).
  const jaar = (d?: string) => {
    const j = d?.match(/\d{4}/)?.[0];
    return j ? parseInt(j) : 9999;
  };
  const kinderen = (relIndex.byParent.get(personId) ?? []).map((r) => r.personB);
  // Oudste gezamenlijke kind; zonder kinderen 9999.
  const gezinsJaar = (spouseId: string) =>
    Math.min(
      ...kinderen
        .filter((k) => (relIndex.byChild.get(k) ?? []).some((r) => r.personA === spouseId))
        .map((k) => jaar(personMap.get(k)?.birthDate)),
      9999
    );

  result.sort((a, b) => {
    // Primary: gender — male first (left), female second (right)
    const gA = a.spouse.gender === 'M' || (a.spouse.gender as string) === 'male' ? 0 : 1;
    const gB = b.spouse.gender === 'M' || (b.spouse.gender as string) === 'male' ? 0 : 1;
    if (gA !== gB) return gA - gB;
    // Secondary: marriage date (oldest first)
    const verschil = jaar(a.marriageDate) - jaar(b.marriageDate);
    if (verschil !== 0) return verschil;
    // Alleen als beide paren kinderen hebben: een eerste huwelijk zonder kinderen
    // vóór een tweede mét kinderen komt veel voor. Anders de invoervolgorde.
    const [ka, kb] = [gezinsJaar(a.spouse.id), gezinsJaar(b.spouse.id)];
    return ka < 9999 && kb < 9999 ? ka - kb : 0;
  });

  return result;
}

interface PartnerChildGroup {
  partnerId: string | null;
  partner: Person | null;
  children: Person[];
}

/**
 * Get children grouped by their other parent (partner).
 * Groups are ordered by marriage date (matching spouse sort order).
 * Children with only one known parent go into an "unknown partner" group.
 */
function getChildrenGroupedByPartner(
  personId: string,
  relIndex: RelIndex,
  personMap: Map<string, Person>,
  events: GenealogyEvent[] = []
): PartnerChildGroup[] {
  const children = getChildren(personId, relIndex, personMap);
  if (children.length === 0) return [];

  // #1590 — de groepvolgorde volgt de huwelijksdatum, dus de huwelijks-
  // GEBEURTENISSEN moeten mee (net als attachSpouses). Zonder events waren alle
  // datums onbekend en volgde de groep de volgorde waarin de server de relaties
  // gaf: kinderen van het 2e huwelijk vóór die van het 1e, en een verkeerde
  // d'Aboville (nulmeting: Hans 1948 = 1.1 vóór Pieter 1942).
  const sortedSpouses = getSpousesSorted(personId, relIndex, personMap, events);
  const spouseOrder = sortedSpouses.map((s) => s.spouse.id);

  // Group children by their other parent
  const groups = new Map<string, Person[]>();

  for (const child of children) {
    const childParentRels = relIndex.byChild.get(child.id) ?? [];
    const otherParentId = childParentRels
      .map((r) => r.personA)
      .find((pid) => pid !== personId && personMap.has(pid));

    const key = otherParentId || '__unknown__';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(child);
  }

  // Build result: known spouses first (in marriage-date order), then unknown
  const result: PartnerChildGroup[] = [];

  for (const spouseId of spouseOrder) {
    if (groups.has(spouseId)) {
      result.push({
        partnerId: spouseId,
        partner: personMap.get(spouseId) || null,
        children: groups.get(spouseId)!,
      });
      groups.delete(spouseId);
    }
  }

  // Remaining: children with non-spouse parent or unknown parent
  for (const [key, groupChildren] of groups) {
    result.push({
      partnerId: key === '__unknown__' ? null : key,
      partner: key === '__unknown__' ? null : personMap.get(key) || null,
      children: groupChildren,
    });
  }

  return result;
}

/**
 * Check if a person has parents in the dataset.
 */
function hasParentsInDataset(
  personId: string,
  relIndex: RelIndex,
  personMap: Map<string, Person>
): boolean {
  const parentRels = relIndex.byChild.get(personId) ?? [];
  return parentRels.some((rel) => personMap.has(rel.personA));
}

/**
 * Check if a person has children in the dataset.
 */
function hasChildrenInDataset(
  personId: string,
  relIndex: RelIndex,
  personMap: Map<string, Person>
): boolean {
  const childRels = relIndex.byParent.get(personId) ?? [];
  return childRels.some((rel) => personMap.has(rel.personB));
}

// ─── Edit-mode Missing-Parent Placeholders (#1089) ───────────────────────────

/**
 * Generate edit-mode "missing parent" PLACEHOLDER nodes for the ancestor views.
 *
 * For each REAL, already-laid-out ancestor node (incl. the proband) that is
 * missing a father and/or a mother, emit a first-class `LayoutNode` placeholder
 * positioned at the Sosa slot the missing parent WOULD occupy. Because the
 * placeholders are returned as ordinary `LayoutNode`s they are fed back into the
 * same per-generation overlap-resolution pass as real/spouse nodes, so the
 * no-overlap guarantee extends to them.
 *
 * Determinism: positions are derived purely from the child node's x + parent
 * role + generation (no Math.random / Date). Same data → identical placeholders.
 *
 * Bounded: only ONE generation of placeholders is emitted above existing real
 * leaf nodes. A placeholder is never given placeholder-parents of its own.
 *
 * Only called when `editPlaceholders` is true AND the view is an ancestor view
 * (PEDIGREE / PATRILINEAL / MATRILINEAL). When false the function is never
 * invoked → byte-identical layout.
 */
function generateParentPlaceholders(
  realNodes: LayoutNode[],
  relIndex: RelIndex,
  personMap: Map<string, Person>,
  viewMode: TreeViewMode,
  depthLimit: number
): LayoutNode[] {
  const placeholders: LayoutNode[] = [];

  // Only ancestor nodes (positive generation) can be missing PARENTS above them.
  // Descendant nodes (negative gen, hourglass / line-view descendants) are
  // excluded — placeholders are an ancestor-direction concept.
  const ancestorNodes = realNodes.filter((n) => n.generation >= 0 && !n.isSpouseNode);

  for (const node of ancestorNodes) {
    const childGen = node.generation;

    // Fix 3 — skip nodes at the depth boundary: their parent generation would be
    // depthLimit + 1, one ring beyond the slider, so no placeholder is wanted.
    if (childGen >= depthLimit) continue;

    const parentGen = childGen + 1;
    const parentDims = getNodeDims(parentGen);

    // Which real parents already exist (filtered by view mode, gender-aware).
    const existingParents = getFilteredParents(node.person.id, relIndex, personMap, viewMode);

    // Fix 1 — slot-aware occupancy for unknown-gender ('U') parents.
    // buildSosaMap assigns an unknown-gender parent to the FATHER slot (2N) when
    // that slot is free, otherwise the mother slot (2N+1). For the common case of
    // a single unknown-gender parent we must treat it as occupying the father slot
    // so that we only emit ONE placeholder (for the mother side), not two.
    //
    // Strategy: determine which slots are already occupied by scanning existingParents
    // in order. The first non-F parent fills the father slot; the first non-M parent
    // fills the mother slot. Unknown-gender ('U') maps to the father slot by default
    // (matching buildSosaMap), unless a real M parent already occupies it.
    let hasFather = existingParents.some(
      (p) => p.gender === 'M' || (p.gender as string) === 'male'
    );
    const hasMother = existingParents.some(
      (p) => p.gender === 'F' || (p.gender as string) === 'female'
    );
    // An unknown-gender parent occupies the father slot when no M parent is present
    // (mirrors buildSosaMap: father slot = 2N, taken first).
    if (
      !hasFather &&
      existingParents.some((p) => p.gender !== 'F' && (p.gender as string) !== 'female')
    ) {
      hasFather = true;
    }

    // Hard cap: never emit more placeholders than there are genuinely empty slots.
    const maxPlaceholders = 2 - existingParents.length;
    if (maxPlaceholders <= 0) continue;

    // Determine which roles are missing, gated by view mode:
    //  - PATRILINEAL: only the father line is relevant → offer a father placeholder.
    //  - MATRILINEAL: only the mother line → mother placeholder.
    //  - PEDIGREE: both father + mother.
    const wantFather = viewMode !== 'MATRILINEAL';
    const wantMother = viewMode !== 'PATRILINEAL';
    const rolesMissing: Array<'father' | 'mother'> = [];
    if (wantFather && !hasFather) rolesMissing.push('father');
    if (wantMother && !hasMother) rolesMissing.push('mother');
    if (rolesMissing.length === 0) continue;

    // Apply the hard cap (existingParents.length=1 → at most 1 placeholder).
    const cappedRoles = rolesMissing.slice(0, maxPlaceholders);

    // Intra-pair distance: how far a father/mother sits from the couple-center,
    // mirroring how real couples are positioned (father left, mother right). When
    // BOTH parents are missing the child sits at the couple-center, so father =
    // center − d/2, mother = center + d/2. When only ONE is missing the existing
    // real parent already holds its side, so the placeholder takes the OTHER slot
    // offset from the child by the same intra-pair distance.
    const intraPairDistance = parentDims.width + parentDims.spouseGap;
    const parentY = getGenY(parentGen);

    for (const role of cappedRoles) {
      // Direct-line node keeps its slot; placeholder goes to the missing side.
      // father → left of child-center, mother → right of child-center.
      const baseX =
        role === 'father' ? node.x - intraPairDistance / 2 : node.x + intraPairDistance / 2;

      // Synthetic, STABLE id derived from child id + role (no randomness).
      const phId = `__placeholder__${node.person.id}__${role}`;
      const phPerson: Person = {
        id: phId,
        projectId: node.person.projectId,
        givenNames: '',
        surname: '',
        gender: role === 'father' ? 'M' : 'F',
        isLiving: false,
      } as Person;

      placeholders.push({
        person: phPerson,
        x: baseX,
        y: parentY,
        generation: parentGen,
        isEndOfLine: false,
        hasMoreData: false,
        spouses: [],
        nodeWidth: parentDims.width,
        isPlaceholder: true,
        placeholderParentRole: role,
        placeholderForChildId: node.person.id,
      });
    }
  }

  return placeholders;
}

/** #1957 — een archiefvoorstel: de lege Sosa-plek boven een eindpunt (proband-relatief). */
export interface VoorstelPlek {
  sleutel: string;
  kindSosa: number;
  rol: 'vader' | 'moeder';
  geslacht: 'M' | 'F';
}

/**
 * #1957 plan 5 (NC p5-rij, 03-11) — een archiefkind in de nakomelingen: een kind dat PeckArchive bij dit
 * ouderpaar vond en dat nog niet in de boom staat. Het telt mee in de indeling als kind van het paar (op
 * geboortejaar tussen de broers en zussen), zodat de kaarten opschuiven; het blijft een voorstelknoop.
 */
export interface NakomKindPlek {
  sleutel: string;
  vaderId: string;
  moederId: string;
  naam: string;
  gebJaar: number | null;
}
export type LaagPlek = VoorstelPlek | NakomKindPlek;

const isNakomKindPlek = (p: LaagPlek): p is NakomKindPlek => 'vaderId' in p;
const NAKOM_KIND_PREFIX = '__nakomkind__';
/** De knoop-id van een archiefkind; de sleutel staat er achter het voorvoegsel. */
export const isNakomKindId = (id: string): boolean => id.startsWith(NAKOM_KIND_PREFIX);

/**
 * De archiefkinderen als personen met een ouderrelatie naar vader en moeder, alleen als beide ouders in
 * de data staan. Alleen voor de indeling: ze komen nooit in de d'Aboville-nummering of een export.
 */
function nakomKindData(plekken: NakomKindPlek[], personMap: Map<string, Person>) {
  const personen: Person[] = [];
  const relaties: Relationship[] = [];
  for (const k of plekken) {
    const vader = personMap.get(k.vaderId);
    if (!vader || !personMap.has(k.moederId)) continue;
    const id = `${NAKOM_KIND_PREFIX}${k.sleutel}`;
    personen.push({
      id,
      projectId: vader.projectId,
      givenNames: k.naam,
      surname: '',
      gender: 'U',
      isLiving: false,
      ...(k.gebJaar ? { birthDate: String(k.gebJaar) } : {}),
    } as Person);
    for (const ouder of [k.vaderId, k.moederId])
      relaties.push({
        id: `${id}|${ouder}`,
        type: 'parent-child',
        personA: ouder,
        personB: id,
      } as Relationship);
  }
  return { personen, relaties };
}

/** #1957 — placeholder of archiefvoorstel: een kaart zonder echte persoon, nooit in een export. */
export const isSpookknoop = (n: LayoutNode): boolean =>
  n.isPlaceholder === true || n.isVoorstel === true;

/**
 * #1957 — archiefvoorstellen als LayoutNodes, geplaatst zoals een placeholder (#1089): op
 * kind.x ∓ (breedte + spouseGap)/2, één rij boven het kind. Alleen generatie 1 boven een eindpunt:
 * het kind is altijd de echte knoop met Sosa `kindSosa` (dus hoogstens één generatie boven de grens
 * van de weergave). Geen echt kind, een bezette plek of een rol die de lijnweergave niet toont →
 * overslaan.
 */
function plaatsVoorstellen(
  nodes: LayoutNode[],
  plekken: VoorstelPlek[],
  viewMode: TreeViewMode
): LayoutNode[] {
  const kinderen = new Map<number, LayoutNode>();
  for (const n of nodes)
    if (n.sosaNumber !== undefined && n.generation >= 0 && !n.isSpouseNode)
      kinderen.set(n.sosaNumber, n);
  const bezet = new Set(nodes.map((n) => n.sosaNumber));
  const voorstellen: LayoutNode[] = [];
  for (const p of plekken) {
    if (viewMode === (p.rol === 'vader' ? 'MATRILINEAL' : 'PATRILINEAL')) continue;
    const sosa = 2 * p.kindSosa + (p.rol === 'moeder' ? 1 : 0);
    const kind = kinderen.get(p.kindSosa);
    if (!kind || bezet.has(sosa)) continue;
    const gen = kind.generation + 1;
    const dims = getNodeDims(gen);
    const role = p.rol === 'vader' ? 'father' : 'mother';
    const d = dims.width + dims.spouseGap;
    const node: LayoutNode = {
      person: {
        id: `__voorstel__${p.sleutel}`,
        projectId: kind.person.projectId,
        givenNames: '',
        surname: '',
        gender: p.geslacht,
        isLiving: false,
      } as Person,
      x: role === 'father' ? kind.x - d / 2 : kind.x + d / 2,
      y: getGenY(gen),
      generation: gen,
      sosaNumber: sosa,
      isEndOfLine: false,
      hasMoreData: false,
      spouses: [],
      nodeWidth: dims.width,
      isVoorstel: true,
      voorstelSleutel: p.sleutel,
      // Zelfde velden als een placeholder: verbindingslijn en overlap-pass vinden zo het kind.
      placeholderParentRole: role,
      placeholderForChildId: kind.person.id,
    };
    voorstellen.push(node);
    bezet.add(sosa);
  }
  return voorstellen;
}

/**
 * #1964 — de voorstellen van de Stamboom als personen op hun Sosa-plek in het rooster (`layoutPedigree`):
 * het kind is een echte, zichtbare voorouder (generatie ≤ de grens), de plek is in de data leeg. Zelfde id
 * als `plaatsVoorstellen`. Geeft Sosa → id; de personen komen in `personMap`.
 */
function voorstelRooster(
  plekken: VoorstelPlek[],
  zichtbaar: Map<number, string>,
  personMap: Map<string, Person>
): Map<number, string> {
  const uit = new Map<number, string>();
  for (const p of plekken) {
    const sosa = 2 * p.kindSosa + (p.rol === 'moeder' ? 1 : 0);
    const kindId = zichtbaar.get(p.kindSosa);
    if (!kindId || zichtbaar.has(sosa) || uit.has(sosa)) continue;
    const id = `__voorstel__${p.sleutel}`;
    personMap.set(id, {
      id,
      projectId: personMap.get(kindId)?.projectId ?? '',
      givenNames: '',
      surname: '',
      gender: p.geslacht,
      isLiving: false,
    } as Person);
    uit.set(sosa, id);
  }
  return uit;
}

// ─── Sosa Number Assignment ──────────────────────────────────────────────────

/**
 * Build a Map<personId, sosaNumber> from the proband through ALL people,
 * independent of which nodes are currently visible in the layout.
 * Uses BFS: father = 2N, mother = 2N+1.
 */
export function buildSosaMap(
  probandId: string,
  personMap: Map<string, Person>,
  relIndex: RelIndex
): Map<string, number> {
  const sosaMap = new Map<string, number>();
  if (!personMap.has(probandId)) return sosaMap;

  sosaMap.set(probandId, 1);
  const queue: string[] = [probandId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const sosa = sosaMap.get(currentId)!;
    const parentRels = relIndex.byChild.get(currentId) ?? [];

    for (const rel of parentRels) {
      const parent = personMap.get(rel.personA);
      if (!parent || sosaMap.has(parent.id)) continue;

      if (parent.gender === 'M') {
        sosaMap.set(parent.id, sosa * 2);
      } else if (parent.gender === 'F') {
        sosaMap.set(parent.id, sosa * 2 + 1);
      } else {
        // Unknown gender: father slot if not taken, else mother
        const fatherSosa = sosa * 2;
        const alreadyHasFather = [...sosaMap.values()].includes(fatherSosa);
        sosaMap.set(parent.id, alreadyHasFather ? sosa * 2 + 1 : sosa * 2);
      }

      queue.push(parent.id);
    }
  }

  return sosaMap;
}

// ─── Sosa Slot Walk (implex-aware) ───────────────────────────────────────────

/** Veiligheidsplafond voor het scherm-pad (V1 had er geen; V2/PDF capt op 2048). */
export const MAX_SCREEN_NODES = 2048;

export interface SosaSlotResult {
  /** sosa → personId. Dezelfde personId mag op meerdere slots staan (= implex). */
  sosaToId: Map<number, string>;
  /** personId → alle Sosa-slots (gesorteerd). Aanwezig (lengte ≥2) ⇒ implex. */
  implexByPerson: Map<string, number[]>;
}

/**
 * Sosa-toekenning die pedigree-collapse (implex) toestaat: BFS per Sosa-slot i.p.v.
 * per persoon. Dezelfde voorouder die via meerdere paden bereikbaar is krijgt
 * meerdere slots. Begrensd door `depthLimit` én `maxNodes` (termineert ook bij een
 * corrupte zelf-cyclus). Registratie gebeurt bij enqueue (cycle-veilig, deterministisch).
 */
export function buildSosaSlots(
  probandId: string,
  personMap: Map<string, Person>,
  relIndex: RelIndex,
  depthLimit: number,
  maxNodes: number = MAX_SCREEN_NODES
): SosaSlotResult {
  const sosaToId = new Map<number, string>();
  const personSlots = new Map<string, number[]>();
  if (!personMap.has(probandId)) return { sosaToId, implexByPerson: new Map() };

  sosaToId.set(1, probandId);
  personSlots.set(probandId, [1]);
  const queue: number[] = [1];

  while (queue.length > 0) {
    if (sosaToId.size >= maxNodes) break;
    const sosa = queue.shift()!;
    const gen = Math.floor(Math.log2(sosa));
    if (gen >= depthLimit) continue; // niet verder omhoog dan de diepte-slider
    const childId = sosaToId.get(sosa)!;
    const parentRels = relIndex.byChild.get(childId) ?? [];

    for (const rel of parentRels) {
      const parent = personMap.get(rel.personA);
      if (!parent) continue;
      let slot: number;
      if (parent.gender === 'M') slot = sosa * 2;
      else if (parent.gender === 'F') slot = sosa * 2 + 1;
      else slot = sosaToId.has(sosa * 2) ? sosa * 2 + 1 : sosa * 2; // onbekend: vader-slot indien vrij
      if (sosaToId.has(slot)) continue; // slot al gevuld (bv. twee vader-relaties)
      if (sosaToId.size >= maxNodes) break;
      sosaToId.set(slot, parent.id);
      const arr = personSlots.get(parent.id);
      if (arr) arr.push(slot);
      else personSlots.set(parent.id, [slot]);
      queue.push(slot); // enqueue = direct registreren
    }
  }

  const implexByPerson = new Map<string, number[]>();
  for (const [pid, slots] of personSlots) {
    if (pid === probandId) continue; // proband (slot 1) is nooit een implex-voorouder van zichzelf
    if (slots.length >= 2)
      implexByPerson.set(
        pid,
        [...slots].sort((a, b) => a - b)
      );
  }
  return { sosaToId, implexByPerson };
}

// ─── Sosa-based Pedigree Layout ─────────────────────────────────────────────

/**
 * Layout a pedigree chart using direct Sosa-number-based slot calculation.
 *
 * Instead of recursive subtree-width computation (which breaks centering with
 * asymmetric subtrees), this function:
 * 1. Assigns evenly-spaced slots for the top generation
 * 2. For each lower generation, x = midpoint of father + mother
 *
 * This guarantees: child centered between parents, no overlaps, no crossed lines.
 */
export function layoutPedigree(
  probandId: string,
  depthLimit: number,
  personMap: Map<string, Person>,
  relIndex: RelIndex,
  sosaMap: Map<string, number>, // pre-computed via buildSosaMap
  relationLines: boolean = false,
  implexSlots?: SosaSlotResult, // flag-aan pad — sosa→pid met duplicaten
  // #1964 — archiefvoorstellen op hun eigen Sosa-plek (ook één generatie boven de grens), zodat het rooster
  // er een generatie voor maakt; de personen staan al in `personMap`.
  voorstelSosa?: Map<number, string>
): SubtreeResult {
  const proband = personMap.get(probandId);
  if (!proband) return { width: 0, nodes: [], links: [] };

  // Invert sosaMap (flag-uit), óf gebruik de implex-slots direct (flag-aan).
  const sosaToId = new Map<number, string>();
  if (implexSlots) {
    for (const [sosa, pid] of implexSlots.sosaToId) sosaToId.set(sosa, pid);
  } else {
    for (const [pid, sosa] of sosaMap) {
      // Only include ancestors within depthLimit
      const gen = Math.floor(Math.log2(sosa));
      if (gen <= depthLimit) sosaToId.set(sosa, pid);
    }
  }
  for (const [sosa, pid] of voorstelSosa ?? []) if (!sosaToId.has(sosa)) sosaToId.set(sosa, pid);

  // Determine the actual max generation that has data (not necessarily depthLimit)
  let maxGen = 0;
  for (const sosa of sosaToId.keys()) {
    const gen = Math.floor(Math.log2(sosa));
    if (gen > maxGen) maxGen = gen;
  }
  if (maxGen === 0 && sosaToId.size <= 1) {
    // Only proband, no ancestors
    const dims = getNodeDims(0);
    const node: LayoutNode = {
      person: proband,
      x: dims.width / 2,
      y: getGenY(0),
      generation: 0,
      isEndOfLine: !hasParentsInDataset(probandId, relIndex, personMap),
      hasMoreData: false,
      spouses: [],
      nodeWidth: dims.width,
    };
    return { width: dims.width, nodes: [node], links: [] };
  }

  // Step 1: Calculate slot positions for the top generation using UNIFORM spacing.
  // Uniform spacing guarantees that midpoint calculations at lower generations
  // produce perfectly centered children — no couple/non-couple gap distinction needed
  // at the top row since the midpoint formula handles couple proximity automatically.
  //
  // #525 H.1 V1 fix — slotPitch volgt nu de V2-formule:
  //   slotPitch = max(renderWidth over alle gens 0..maxGen) + MIN_SIBLING_GAP_REG
  // Eerder gebruikte V1 de legacy layout-dim (COMPACT_NODE_WIDTH=111) terwijl
  // cards op render-tijd 220/180/130 wide zijn (DETAILED/STANDARD/COMPACT).
  // Dat gaf 61px overlap per sibling-pair op gen 4-5 en 11px op gen 6+.
  // De render-width-aware formule garandeert dat de effectieve afstand
  // tussen siblings op elke gen N (= slotPitch × 2^(maxGen-N)) altijd
  // ≥ maxRenderWidth + minGap is.
  const topDims = getNodeDims(maxGen);
  const totalTopSlots = Math.pow(2, maxGen);
  let maxRenderWidth = 0;
  for (let g = 0; g <= maxGen; g++) {
    const w = computeCardWidthForLevel(assignCardLevel(g), []);
    if (w > maxRenderWidth) maxRenderWidth = w;
  }
  // slotPitch wordt na pair-clustering nog door diagnostic-tooling gelezen;
  // representeert nu een minimum-pitch-constraint i.p.v. de daadwerkelijke
  // spacing. We berekenen en bewaren 'm voor backwards-compat met tests.
  const slotPitch = maxRenderWidth + MIN_SIBLING_GAP_REG;
  const topRowWidth = totalTopSlots * topDims.width + (totalTopSlots - 1) * topDims.spouseGap;
  void slotPitch;

  const xPositions = new Map<number, number>(); // sosa → center x

  // #525 pair-clustering (user-request 2026-05-19): vader+moeder van elk top-gen-
  // paar dicht bij elkaar (intra-pair = cardWidthTop + spouseGap). Inter-pair =
  // pairWidth + minGap. Cascade-effect via midpoint-formule krimpt hele boom ~50%.
  //
  // BELANGRIJK: V1's /tree-view rendert cards met `node.nodeWidth = topDims.width`
  // (= 230 gen 0-3, 111 gen 4+). De PDF/SVG-renderpad gebruikt cardLevel-widths
  // (220/180/130). We gebruiken hier `topDims.width` zodat de edge-to-edge gap
  // matcht met de daadwerkelijke render-breedte. V2 (PDF) gebruikt zijn eigen
  // cardLevel-based berekening verderop in computeTreeLayoutPureV2.
  const TOP_GEN_SPOUSE_GAP = 10;
  const cardWidthTop = topDims.width;
  const intraPairDistance = cardWidthTop + TOP_GEN_SPOUSE_GAP;
  const pairWidth = cardWidthTop * 2 + TOP_GEN_SPOUSE_GAP;
  const pairPitch = pairWidth + MIN_SIBLING_GAP_REG;
  const pairCount = totalTopSlots / 2;
  for (let p = 0; p < pairCount; p++) {
    const pairCenter = p * pairPitch + pairWidth / 2;
    const fatherSosa = Math.pow(2, maxGen) + p * 2;
    const motherSosa = fatherSosa + 1;
    xPositions.set(fatherSosa, pairCenter - intraPairDistance / 2);
    xPositions.set(motherSosa, pairCenter + intraPairDistance / 2);
  }

  // Step 2: Calculate x for all lower generations (top-down) via midpoint
  for (let gen = maxGen - 1; gen >= 0; gen--) {
    const slotsInGen = Math.pow(2, gen);
    for (let i = 0; i < slotsInGen; i++) {
      const sosa = Math.pow(2, gen) + i;
      const fatherSosa = sosa * 2;
      const motherSosa = sosa * 2 + 1;

      const fatherX = xPositions.get(fatherSosa);
      const motherX = xPositions.get(motherSosa);

      if (fatherX !== undefined && motherX !== undefined) {
        xPositions.set(sosa, (fatherX + motherX) / 2);
      } else if (fatherX !== undefined) {
        xPositions.set(sosa, fatherX);
      } else if (motherX !== undefined) {
        xPositions.set(sosa, motherX);
      }
    }
  }

  // Step 3: Build LayoutNodes for all existing ancestors
  const nodes: LayoutNode[] = [];
  const nodeMap = new Map<number, LayoutNode>(); // sosa → node

  for (const [sosa, pid] of sosaToId) {
    const person = personMap.get(pid);
    if (!person) continue;

    const gen = Math.floor(Math.log2(sosa));
    const dims = getNodeDims(gen);
    const cx = xPositions.get(sosa);
    if (cx === undefined) continue;

    const atLimit = gen >= depthLimit;
    const hasParents = hasParentsInDataset(pid, relIndex, personMap);

    const node: LayoutNode = {
      person,
      x: cx,
      y: getGenY(gen),
      generation: gen,
      isEndOfLine: !hasParents,
      hasMoreData: hasParents && atLimit,
      spouses: [],
      nodeWidth: dims.width,
    };
    // Sosa-nummer per slot (bij implex deelt een persoon person.id maar niet de sosa).
    node.sosaNumber = sosa;
    if (implexSlots) {
      const implexSosa = implexSlots.implexByPerson.get(pid);
      if (implexSosa && implexSosa.length > 1) {
        node.isImplex = true;
        node.implexSosaNumbers = implexSosa;
        node.instanceKey = `${pid}#${sosa}`;
      }
    }
    nodes.push(node);
    nodeMap.set(sosa, node);
  }

  // Step 4: Build parent-child links
  const links: LayoutLink[] = [];
  for (const [sosa, node] of nodeMap) {
    if (sosa <= 1) continue; // proband has no "child" link in ancestor direction

    // Find the child (sosa / 2, rounded down)
    const childSosa = Math.floor(sosa / 2);
    const childNode = nodeMap.get(childSosa);
    if (!childNode) continue;
    // #1964 — naar een voorstel loopt de gestippelde lijn van TreeLinks, geen gewone ouder-kindlijn.
    if (voorstelSosa?.has(sosa)) continue;

    links.push({
      sourceId: childNode.person.id,
      targetId: node.person.id,
      sourceX: childNode.x,
      sourceY: childNode.y,
      targetX: node.x,
      targetY: node.y,
      type: 'parent-child',
      pediType: pediTypeFor(node.person.id, childNode.person.id, relIndex, relationLines),
    });
  }

  // Ensure proband is the first node (contract for parent root lookup)
  const probandIdx = nodes.findIndex((n) => n.person.id === probandId);
  if (probandIdx > 0) {
    const [probandNode] = nodes.splice(probandIdx, 1);
    nodes.unshift(probandNode);
  }

  return { width: topRowWidth, nodes, links };
}

// ─── Max Ancestor Depth ─────────────────────────────────────────────────────

/**
 * Compute the maximum ancestor depth from a given person using DFS.
 * Tracks the deepest path per person so pedigree collapse (same ancestor
 * reachable via multiple paths) correctly reports the LONGEST path.
 */
function computeMaxAncestorDepth(
  startId: string,
  personMap: Map<string, Person>,
  relIndex: RelIndex
): number {
  const maxDepthOf = new Map<string, number>();
  let globalMax = 0;
  const stack: [string, number][] = [[startId, 0]];

  while (stack.length > 0) {
    const [id, depth] = stack.pop()!;

    // Only process if this is a deeper path than previously found
    if (maxDepthOf.has(id) && maxDepthOf.get(id)! >= depth) continue;
    maxDepthOf.set(id, depth);
    if (depth > globalMax) globalMax = depth;

    const parentRels = relIndex.byChild.get(id) ?? [];
    for (const rel of parentRels) {
      if (!personMap.has(rel.personA)) continue;
      const prevDepth = maxDepthOf.get(rel.personA);
      if (prevDepth === undefined || prevDepth < depth + 1) {
        stack.push([rel.personA, depth + 1]);
      }
    }
  }

  return globalMax;
}

/**
 * Compute the maximum descendant depth from a given person using DFS.
 * Mirror of computeMaxAncestorDepth — needed for DESCENDANTS view to bound
 * the generation slider to the actual data depth instead of the rendered
 * (already-sliced) node set, which would create a feedback loop.
 */
function computeMaxDescendantDepth(
  startId: string,
  personMap: Map<string, Person>,
  relIndex: RelIndex
): number {
  const maxDepthOf = new Map<string, number>();
  let globalMax = 0;
  const stack: [string, number][] = [[startId, 0]];

  while (stack.length > 0) {
    const [id, depth] = stack.pop()!;

    if (maxDepthOf.has(id) && maxDepthOf.get(id)! >= depth) continue;
    maxDepthOf.set(id, depth);
    if (depth > globalMax) globalMax = depth;

    const childRels = relIndex.byParent.get(id) ?? [];
    for (const rel of childRels) {
      if (!personMap.has(rel.personB)) continue;
      const prevDepth = maxDepthOf.get(rel.personB);
      if (prevDepth === undefined || prevDepth < depth + 1) {
        stack.push([rel.personB, depth + 1]);
      }
    }
  }

  return globalMax;
}

/**
 * Append descendants (children, grandchildren, ...) below the focus person
 * in an ancestor-oriented layout (PEDIGREE / PATRILINEAL / MATRILINEAL).
 *
 * Mutates `result` in place: adds descendant nodes (with mirrored y so they
 * render below the focus) and parent-child links. Skips the descendants'
 * focus-person node since it is already present in the ancestor layout.
 *
 * No-op when descendantDepth <= 0 or when the focus person has no children.
 */
function appendDescendantsToLayout(
  result: { nodes: LayoutNode[]; links: LayoutLink[] },
  focusPersonId: string,
  descendantDepth: number,
  personMap: Map<string, Person>,
  relIndex: RelIndex,
  events: GenealogyEvent[] = [],
  relationLines: boolean = false,
  collapsedPartnerNodes?: Set<string>,
  wisselExtra: number = 0
): void {
  if (descendantDepth <= 0) return;

  const focusNode = result.nodes.find((n) => n.person.id === focusPersonId);
  if (!focusNode) return;

  const descResult = layoutDescendants(
    focusPersonId,
    0,
    descendantDepth,
    personMap,
    relIndex,
    new Set<string>(),
    false,
    relationLines,
    events,
    undefined,
    // #1793 — ook hier het anker midden tussen zijn partners (zelfde layout als Nakom.).
    {
      mainIds: new Set([
        ...result.nodes.map((n) => n.person.id),
        ...nakomelingIds(focusPersonId, descendantDepth, personMap, relIndex, events),
      ]),
      ingeklapt: collapsedPartnerNodes,
      extra: wisselExtra,
    }
  );
  const descFocusNode = descResult.nodes.find((n) => n.person.id === focusPersonId);
  if (!descFocusNode || descResult.nodes.length <= 1) return;
  const focusPlan = partnerPlan.get(descFocusNode);
  if (focusPlan) partnerPlan.set(focusNode, focusPlan);

  const offsetX = focusNode.x - descFocusNode.x;
  const offsetY = focusNode.y - descFocusNode.y;

  for (const node of descResult.nodes) {
    if (node.person.id === focusPersonId) continue;
    node.x += offsetX;
    node.y += offsetY;
    // Negate generation so the canvas treats them as descendants of the focus
    // (negative gen = below in this layout's coordinate system).
    node.generation = -node.generation;
    result.nodes.push(node);
  }
  for (const link of descResult.links) {
    link.sourceX += offsetX;
    link.targetX += offsetX;
    link.sourceY += offsetY;
    link.targetY += offsetY;
    // #1041 — family-unit links hebben extra positie-data (childXs, busbarY)
    // die ook moet shiften, anders staat de busbar verkeerd t.o.v. de
    // verschoven kinderen in pedigree-hourglass-mode.
    if (link.childXs) link.childXs = link.childXs.map((x) => x + offsetX);
    if (link.busbarY !== undefined) link.busbarY += offsetY;
    result.links.push(link);
  }
}

// ─── Ancestor Count ─────────────────────────────────────────────────────────

/**
 * Recursively count all ancestors above a person in the full dataset.
 * Used to show "+X" badge on nodes at the depth limit.
 */
function countAncestorsAbove(
  personId: string,
  relIndex: RelIndex,
  personMap: Map<string, Person>,
  visited: Set<string>
): number {
  if (visited.has(personId)) return 0;
  visited.add(personId);

  const parentRels = relIndex.byChild.get(personId) ?? [];
  let count = 0;

  for (const rel of parentRels) {
    if (!personMap.has(rel.personA) || visited.has(rel.personA)) continue;
    count += 1;
    count += countAncestorsAbove(rel.personA, relIndex, personMap, visited);
  }

  return count;
}

// ─── Ancestor Layout (Bottom-Up) ────────────────────────────────────────────

/**
 * Recursively compute compact ancestor layout.
 * Returns a subtree with all nodes positioned relative to local origin (0,0).
 * The current person is centered horizontally within its subtree.
 */
function layoutAncestors(
  personId: string,
  generation: number,
  depthLimit: number,
  personMap: Map<string, Person>,
  relIndex: RelIndex,
  viewMode: TreeViewMode,
  visited: Set<string>,
  relationLines: boolean = false
): SubtreeResult {
  const person = personMap.get(personId);
  if (!person) return { width: 0, nodes: [], links: [] };

  // Prevent infinite loops from circular relationships
  if (visited.has(personId)) return { width: 0, nodes: [], links: [] };
  visited.add(personId);

  const hasParents = hasParentsInDataset(personId, relIndex, personMap);
  const atDepthLimit = generation >= depthLimit;

  // Determine end-of-line and has-more-data status
  const isEndOfLine = !hasParents;
  const hasMoreData = hasParents && atDepthLimit;

  const dims = getNodeDims(generation);

  if (atDepthLimit || !hasParents) {
    // Leaf node: just this person
    const node: LayoutNode = {
      person,
      x: dims.width / 2,
      y: getGenY(generation),
      generation,
      isEndOfLine,
      hasMoreData,
      spouses: [],
      nodeWidth: dims.width,
    };
    return { width: dims.width, nodes: [node], links: [] };
  }

  // Get parents (filtered by view mode)
  const parents = getFilteredParents(personId, relIndex, personMap, viewMode);

  if (parents.length === 0) {
    // No parents after filtering (e.g. patrilineal but only mother exists)
    const node: LayoutNode = {
      person,
      x: dims.width / 2,
      y: getGenY(generation),
      generation,
      isEndOfLine: true,
      hasMoreData: false,
      spouses: [],
      nodeWidth: dims.width,
    };
    return { width: dims.width, nodes: [node], links: [] };
  }

  // Recursively layout each parent subtree
  const subtreeResults: (SubtreeResult | null)[] = [];
  for (const parent of parents) {
    const result = layoutAncestors(
      parent.id,
      generation + 1,
      depthLimit,
      personMap,
      relIndex,
      viewMode,
      visited,
      relationLines
    );
    subtreeResults.push(result.width > 0 ? result : null);
  }

  // Calculate total width from parent subtrees
  // Use the H_GAP appropriate for the parent generation level
  const parentDims = getNodeDims(generation + 1);
  const validResults = subtreeResults.filter((r): r is SubtreeResult => r !== null);
  let totalParentWidth = 0;
  for (let i = 0; i < validResults.length; i++) {
    // Parents are a couple — use tight spouse gap, not sibling hGap
    if (i > 0) totalParentWidth += parentDims.spouseGap;
    totalParentWidth += validResults[i].width;
  }

  const subtreeWidth = Math.max(dims.width, totalParentWidth);

  // Create the current person's node, centered in the subtree
  const thisNode: LayoutNode = {
    person,
    x: subtreeWidth / 2,
    y: getGenY(generation),
    generation,
    isEndOfLine,
    hasMoreData,
    spouses: [],
    nodeWidth: dims.width,
  };

  const allNodes: LayoutNode[] = [thisNode];
  const allLinks: LayoutLink[] = [];

  // Position parent subtrees side by side, centered within subtreeWidth
  const parentBlockOffset = (subtreeWidth - totalParentWidth) / 2;
  let xCursor = parentBlockOffset;

  for (const result of validResults) {
    // Offset all nodes in this subtree
    for (const node of result.nodes) {
      node.x += xCursor;
    }
    // Offset all links in this subtree
    for (const link of result.links) {
      link.sourceX += xCursor;
      link.targetX += xCursor;
    }

    // The root of this subtree is the first node (the parent)
    const parentRootNode = result.nodes[0];
    if (parentRootNode) {
      // Create link from current person to parent
      allLinks.push({
        sourceId: thisNode.person.id,
        targetId: parentRootNode.person.id,
        sourceX: thisNode.x,
        sourceY: thisNode.y,
        targetX: parentRootNode.x,
        targetY: parentRootNode.y,
        type: 'parent-child',
        pediType: pediTypeFor(
          parentRootNode.person.id,
          thisNode.person.id,
          relIndex,
          relationLines
        ),
      });
    }

    allNodes.push(...result.nodes);
    allLinks.push(...result.links);

    xCursor += result.width + parentDims.spouseGap;
  }

  // Compact pass: slide the second (mother's) subtree leftward to bring the couple
  // closer together, but only as far as the closest nodes on any shared generation
  // allow without overlapping.
  if (validResults.length === 2) {
    const leftNodes = validResults[0].nodes;
    const rightNodes = validResults[1].nodes;

    // Find the maximum shift that doesn't cause overlap on any generation row.
    // For each row that has nodes in both subtrees, calculate the available slack.
    const leftByY = new Map<number, number>(); // y → max x (rightmost edge of left subtree)
    for (const n of leftNodes) {
      const yKey = Math.round(n.y);
      const rightEdge = n.x + (n.nodeWidth || parentDims.width) / 2;
      leftByY.set(yKey, Math.max(leftByY.get(yKey) ?? -Infinity, rightEdge));
    }

    let maxSafeShift = Infinity;
    for (const n of rightNodes) {
      const yKey = Math.round(n.y);
      const leftEdgeOfLeft = leftByY.get(yKey);
      if (leftEdgeOfLeft === undefined) continue; // no left-subtree node on this row
      const leftEdge = n.x - (n.nodeWidth || parentDims.width) / 2;
      const gap = leftEdge - leftEdgeOfLeft;
      // Determine target gap: if these two nodes are the couple (the root nodes),
      // use spouseGap; otherwise use hGap to prevent non-couple overlap.
      const isRootPair =
        n === rightNodes[0] &&
        leftByY.get(yKey) === leftNodes[0].x + (leftNodes[0].nodeWidth || parentDims.width) / 2;
      const genDims = getNodeDims(Math.abs(n.generation));
      const targetGap = isRootPair ? genDims.spouseGap : genDims.hGap;
      const available = gap - targetGap;
      if (available < maxSafeShift) maxSafeShift = available;
    }

    if (maxSafeShift > 1 && maxSafeShift < Infinity) {
      for (const node of rightNodes) node.x -= maxSafeShift;
      for (const link of validResults[1].links) {
        link.sourceX -= maxSafeShift;
        link.targetX -= maxSafeShift;
      }
    }
  }

  return { width: subtreeWidth, nodes: allNodes, links: allLinks };
}

// ─── Descendant Layout (Top-Down) ───────────────────────────────────────────

/** #1806 — hebben `a` en `b` (in de data) minstens één gezamenlijk kind? */
function gezamenlijkKind(a: string, b: string, relIndex: RelIndex): boolean {
  return (relIndex.byParent.get(a) ?? []).some((r) =>
    (relIndex.byChild.get(r.personB) ?? []).some((q) => q.personA === b)
  );
}

/**
 * Ingeklapte partners (≥ 2). #1806 (keuze Twan, ronde 6): alle nakomelingen blijven te zien,
 * dus ook de partners met wie de persoon een kind heeft; alleen kinderloze partners
 * verdwijnen (de +N-badge/knop).
 */
function zichtbarePartners<T extends Person | { spouse: Person }>(
  personId: string,
  partners: T[],
  ingeklapt: Set<string> | undefined,
  relIndex: RelIndex
): T[] {
  if (!ingeklapt?.has(personId) || partners.length < 2) return partners;
  return partners.filter((s) =>
    gezamenlijkKind(personId, 'spouse' in s ? s.spouse.id : s.id, relIndex)
  );
}

/**
 * #1606 — de enige beslisser voor de partnerwissel (attachSpouses) én de ruimte die
 * layoutDescendants daarvoor reserveert: een vrouw van wie de eerste partner die NÁÁST
 * haar komt een man is, schuift één plek op en hij staat links. `naast`: alleen de partners
 * die naast haar komen — niet wie elders in de boom staat (neef-nichthuwelijk) of al een
 * kaart heeft bij een eerdere partner (#1807, twee zussen met dezelfde man).
 * #1793 — bij twee of meer partners naast haar: haar 1e man links, de 2e rechts (zie
 * partnerPlekken).
 */
function wisseltEerstePartner(person: Person, naast: Person[]): boolean {
  const eerste = naast[0];
  return (
    (person.gender === 'F' || (person.gender as string) === 'female') &&
    (eerste?.gender === 'M' || (eerste?.gender as string) === 'male')
  );
}

/**
 * #1793 (review Twan, NC-ronde 16 okt, "man in het midden") — twee of meer partners naast
 * het anker: het anker staat in het midden, de 1e partner rechts, de 2e links, de 3e
 * verder rechts, de 4e verder links. Wisselt het anker (#1606, een vrouw van wie de 1e
 * partner een man is) dan gespiegeld: de 1e links. `rechtsExtra` (de inklapknop op het
 * scherm, #1606 ronde 2) komt in de opening rechts van het anker.
 * @returns partner-id → x t.o.v. het midden van de ankerkaart.
 */
function partnerPlekken(
  ids: string[],
  wisselt: boolean,
  unit: number,
  rechtsExtra: number
): Map<string, number> {
  return new Map(
    ids.map((id, i) => {
      const plek = Math.floor(i / 2) + 1;
      return [id, (i % 2 === 0) !== wisselt ? plek * unit + rechtsExtra : -plek * unit];
    })
  );
}

/**
 * #1793 — de partnerplekken die layoutDescendants heeft gereserveerd; attachSpouses zet
 * de partners daar neer. Op de knoop zelf, zodat de zandloper hem kan overnemen.
 */
const partnerPlan = new WeakMap<LayoutNode, Map<string, number>>();

/** #1606 — de personen die layoutDescendants plaatst (zelfde volgorde en dieptegrens). */
function nakomelingIds(
  rootId: string,
  depthLimit: number,
  personMap: Map<string, Person>,
  relIndex: RelIndex,
  events: GenealogyEvent[]
): Set<string> {
  const ids = new Set<string>();
  const loop = (id: string, generation: number) => {
    if (!personMap.has(id) || ids.has(id)) return;
    ids.add(id);
    if (generation >= depthLimit) return;
    for (const groep of getChildrenGroupedByPartner(id, relIndex, personMap, events))
      for (const kind of groep.children) loop(kind.id, generation + 1);
  };
  loop(rootId, 0);
  return ids;
}

/** Partners naast de kaart in layoutDescendants; zie de parameter `naast` daar. */
interface PartnersNaast {
  mainIds: Set<string>;
  ingeklapt?: Set<string>;
  extra?: number;
  ookBladen?: boolean;
  geplaatst?: Set<string>;
  verwijs?: boolean;
  /** #1807 ronde 6 — broers/zussen met dezelfde partner: persoon → afspraak. */
  gedeeld?: Map<string, GedeeldePartner>;
}

/**
 * #1807 ronde 6 (keuze Twan) — twee broers/zussen met dezelfde partner staan naast elkaar met
 * die partner ertussen. `zijde`: kant van de partner t.o.v. deze persoon. De eerste (zonder
 * `met`) heeft zijn echte kaart; bij de tweede (`met` = de eerste) staat hij er alleen in het
 * plan, voor de kant van haar gezin.
 */
interface GedeeldePartner {
  partner: string;
  zijde: 1 | -1;
  met?: string;
}

/** De partners die naast de kaart komen: niet wie elders in de boom of al naast een eerdere kaart staat. */
function partnersErnaast(
  personId: string,
  relIndex: RelIndex,
  personMap: Map<string, Person>,
  events: GenealogyEvent[],
  naast: PartnersNaast
): Person[] {
  return zichtbarePartners(
    personId,
    getSpousesSorted(personId, relIndex, personMap, events).map((s) => s.spouse),
    naast.ingeklapt,
    relIndex
  ).filter((s) => (naast.verwijs || !naast.mainIds.has(s.id)) && !naast.geplaatst?.has(s.id));
}

/**
 * #1807 ronde 6 — zet broers/zussen met dezelfde (niet zelf in de boom staande) partner naast
 * elkaar: de tweede direct naast de eerste, aan de kant waar de partner bij de eerste staat.
 * Legt de afspraak vast in `naast.gedeeld`; de d'Aboville-nummering kijkt hier niet naar.
 */
function zussenNaastElkaar(
  kinderen: Person[],
  relIndex: RelIndex,
  personMap: Map<string, Person>,
  events: GenealogyEvent[],
  naast: PartnersNaast
): Person[] {
  const gedeeld = (naast.gedeeld ??= new Map());
  const ernaast = new Map(
    kinderen.map((k) => [k.id, partnersErnaast(k.id, relIndex, personMap, events, naast)])
  );
  const volgorde = [...kinderen];
  for (let i = 0; i < volgorde.length; i++) {
    const eerste = volgorde[i];
    if (gedeeld.has(eerste.id)) continue;
    const lijst = ernaast.get(eerste.id)!;
    for (let j = i + 1; j < volgorde.length; j++) {
      const tweede = volgorde[j];
      if (gedeeld.has(tweede.id)) continue;
      const partner = lijst.find(
        (s) => !naast.mainIds.has(s.id) && ernaast.get(tweede.id)!.some((t) => t.id === s.id)
      );
      if (!partner) continue;
      // De kant waar hij ook zonder de tweede bij de eerste zou staan (partnerPlekken, #1606).
      const plek = partnerPlekken(
        lijst.map((s) => s.id),
        wisseltEerstePartner(eerste, lijst),
        1,
        0
      ).get(partner.id)!;
      const zijde = plek > 0 ? 1 : -1;
      gedeeld.set(eerste.id, { partner: partner.id, zijde });
      gedeeld.set(tweede.id, { partner: partner.id, zijde: zijde > 0 ? -1 : 1, met: eerste.id });
      volgorde.splice(j, 1);
      volgorde.splice(i + 1, 0, tweede);
      break;
    }
  }
  return volgorde;
}

/** Schuif een subtree horizontaal (kaarten, lijnen, kinder-x'en van gezinslijnen). */
function schuifSubtree(r: SubtreeResult, d: number): void {
  for (const k of r.nodes) k.x += d;
  for (const l of r.links) {
    l.sourceX += d;
    l.targetX += d;
    if (l.childXs) l.childXs = l.childXs.map((x) => x + d);
  }
}

/**
 * Recursively compute compact descendant layout.
 * Children are placed below the parent (positive y direction).
 */
function layoutDescendants(
  personId: string,
  generation: number,
  depthLimit: number,
  personMap: Map<string, Person>,
  relIndex: RelIndex,
  visited: Set<string>,
  partnerFootprint: boolean = false,
  relationLines: boolean = false,
  events: GenealogyEvent[] = [],
  partnerGap?: number,
  // Partners naast de kaart. `mainIds`: wie zelf in de boom staat (neef-nichthuwelijk)
  // komt er niet naast — behalve met `verwijs` (#1808, Nakomelingen): dan als verwijskaart
  // op een gewone partnerplek (attachSpouses). `ingeklapt`: partners ingeklapt (#1609).
  // `extra`: ruimte tussen haar en de man rechts naast haar (#1606 ronde 2, alleen het
  // scherm). `ookBladen`: ook kaarten zonder getoonde kinderen krijgen hun partners
  // (DESCENDANTS); in de zandloper staan daar geen partners naast. `geplaatst` (#1807): partners die al naast
  // een eerder geplaatste kaart staan (een man die twee zussen trouwde) — geen tweede plek.
  // Zelfde volgorde als attachSpouses (createdSpouseIds): de knopen in preorder.
  naast?: PartnersNaast
): SubtreeResult {
  const person = personMap.get(personId);
  if (!person) return { width: 0, nodes: [], links: [] };

  if (visited.has(personId)) return { width: 0, nodes: [], links: [] };
  visited.add(personId);

  const dims = getNodeDims(generation);

  // #1009 koppel-centrering: in DESCENDANTS plaatst attachSpouses een partner
  // RECHTS van de bloedlijn-kaart. We reserveren daarom de volle PAAR-breedte
  // (twee kaarten + spouseGap) in de subtree, en centreren het paar — niet alleen
  // de bloedlijn-kaart — boven het kinderblok. `coupleShift` verschuift de
  // bloedlijn-kaart links van het paar-midden. Geldt voor leaf én niet-leaf, zodat
  // ook de onderste generatie netjes in zijn slot centreert (niet naar rechts
  // overschiet). Personen zonder partner: shift 0, breedte = één kaart.
  // #1089 — flag UIT: reserveer (zoals voorheen) ruimte voor één partner.
  // Flag AAN (tree.partnerSpacing): reserveer de VOLLEDIGE voetafdruk van álle
  // getoonde partners en centreer de hele groep over het kinderblok, analoog aan
  // de PEDIGREE-fix. attachSpouses plaatst de partners rechts van de anker-kaart.
  // #1609 / #1806 — ingeklapt: alleen de partners met een gezamenlijk kind staan er.
  // #1793 — twee of meer partners naast de kaart: zie `plan` hieronder.
  const shownSpouseCount = zichtbarePartners(
    personId,
    getSpouses(personId, relIndex, personMap),
    naast?.ingeklapt,
    relIndex
  ).filter((s) => !naast?.geplaatst?.has(s.id)).length;
  // #1600 — parenteel-kaarten (partnerGap): altijd de volle voetafdruk, anders
  // schoof een broer/zus tussen een persoon en haar 2e partner.
  const reservedPartners =
    partnerFootprint || partnerGap !== undefined ? shownSpouseCount : Math.min(shownSpouseCount, 1);
  const unit = dims.width + (partnerGap ?? dims.spouseGap);
  const coupleShift = (reservedPartners * unit) / 2;
  const coupleWidth = dims.width + reservedPartners * unit;

  // Group children by partner for multi-partner layout
  const partnerGroups = getChildrenGroupedByPartner(personId, relIndex, personMap, events);
  const hasChildren = partnerGroups.some((g) => g.children.length > 0);
  const atDepthLimit = generation >= depthLimit;
  const multiplePartners = partnerGroups.length > 1;

  const isEndOfLine = !hasChildren;
  const hasMoreData = hasChildren && atDepthLimit;

  // #1793 — twee of meer partners naast de kaart: de kaart in het midden, de partners
  // afwisselend rechts en links (partnerPlekken), de volle voetafdruk gereserveerd en elk
  // gezin aan de kant van het eigen paar. attachSpouses volgt dit plan (partnerPlan).
  // getSpousesSorted, niet getSpouses: dezelfde partners (ook uit huwelijksevents) en
  // dezelfde volgorde als attachSpouses.
  let plan: Map<string, number> | undefined;
  // #1807 ronde 6 — gedeelde partner met een broer/zus (zussenNaastElkaar). Bij de tweede
  // staat hij alleen in het plan (de kant van haar gezin), zonder eigen plek of kaart.
  const gedeeld = naast?.gedeeld?.get(personId);
  if (naast && (naast.ookBladen || (hasChildren && !atDepthLimit))) {
    const ernaast = partnersErnaast(personId, relIndex, personMap, events, naast);
    const geplaatst = (naast.geplaatst ??= new Set());
    // Een verwijskaart staat bij elk van beide verwanten; alleen echte kaarten tellen.
    for (const s of ernaast) if (!naast.mainIds.has(s.id)) geplaatst.add(s.id);
    if (gedeeld) {
      // De gedeelde partner aan zijn kant, de andere partners aan de andere kant.
      const anderen = ernaast.filter((s) => s.id !== gedeeld.partner);
      plan = new Map([
        [gedeeld.partner, gedeeld.zijde * unit],
        ...anderen.map((s, k): [string, number] => [s.id, -gedeeld.zijde * (k + 1) * unit]),
      ]);
    } else {
      // De inklapknop steekt rechts uit de kaart (TreeNode). Staat er een partner rechts —
      // na de wissel (#1606) of bij een man in het midden (#1793) — dan krijgt de opening
      // rechts de ruimte voor knop en hart (alleen het scherm geeft `extra` mee).
      const alle = getSpousesSorted(personId, relIndex, personMap, events);
      const knop =
        alle.length >= 2 && alle.some((s) => !gezamenlijkKind(personId, s.spouse.id, relIndex));
      const wisselt = wisseltEerstePartner(person, ernaast);
      // Eén partner na de wissel: die route (attachSpouses, #1606) regelt de ruimte al.
      if (ernaast.length >= 2 || (knop && ernaast.length === 1 && !wisselt)) {
        plan = partnerPlekken(
          ernaast.map((s) => s.id),
          wisselt,
          unit,
          wisselt || knop ? (naast.extra ?? 0) : 0
        );
      }
    }
  }
  const plekken = plan && [...plan].filter(([id]) => !gedeeld?.met || id !== gedeeld.partner);
  const planRand = plekken && [
    Math.min(0, ...plekken.map(([, x]) => x)) - dims.width / 2,
    Math.max(0, ...plekken.map(([, x]) => x)) + dims.width / 2,
  ];

  if (atDepthLimit || !hasChildren) {
    // Leaf node — reserveer paar-breedte zodat de bloedlijn-kaart links en de
    // (later toegevoegde) partner rechts samen binnen het slot [0, coupleWidth]
    // gecentreerd staan. De bloedlijn-kaart blijft op dims.width/2 (= linkerkaart);
    // het paar-midden ligt op coupleWidth/2.
    const node: LayoutNode = {
      person,
      x: planRand ? -planRand[0] : dims.width / 2,
      // nc#537: descendants gebruiken positieve generaties intern (depth-limit
      // + getNodeDims via Math.abs), maar getGenY verwacht NEGATIEVE generatie
      // voor "afdalend" (positieve Y, onder de focus). Anders verschijnen
      // kinderen BOVEN de focus-person (upside-down tree).
      y: getGenY(-generation),
      generation,
      isEndOfLine,
      hasMoreData,
      spouses: [],
      nodeWidth: dims.width,
    };
    if (!planRand) return { width: coupleWidth, nodes: [node], links: [] };
    partnerPlan.set(node, plan!);
    return { width: planRand[1] - planRand[0], nodes: [node], links: [] };
  }

  // Build ordered children with group membership for half-sibling marking
  interface ChildEntry {
    result: SubtreeResult;
    groupIdx: number;
    partnerId: string | null;
    isHalfSibling: boolean;
    /** De kinderen in deze subtree (twee bij zussen met dezelfde partner, #1807). */
    wortels: LayoutNode[];
  }

  const childEntries: ChildEntry[] = [];
  let firstNonEmptyGroupSeen = false;
  let groupIdx = 0;

  for (const group of partnerGroups) {
    const kinderen = naast?.ookBladen
      ? zussenNaastElkaar(group.children, relIndex, personMap, events, naast)
      : group.children;
    for (const child of kinderen) {
      const result = layoutDescendants(
        child.id,
        generation + 1,
        depthLimit,
        personMap,
        relIndex,
        visited,
        partnerFootprint,
        relationLines,
        events,
        partnerGap,
        naast
      );
      // #1807 ronde 6 — de tweede zus naast de eerste: haar partnerplek in het plan valt op
      // zijn echte kaart. Eén subtree, zodat de broers en zussen eromheen gewoon aansluiten.
      // Onder de kaarten blijven de gezinnen gescheiden: elk hangt aan de eigen kant van hem.
      const g = naast?.gedeeld?.get(child.id);
      const vorige = childEntries[childEntries.length - 1];
      if (g?.met && result.width > 0 && vorige?.wortels[0].person.id === g.met) {
        const [k1, k2] = [vorige.wortels[0], result.nodes[0]];
        const o =
          k1.x +
          (partnerPlan.get(k1)?.get(g.partner) ?? 0) -
          (partnerPlan.get(k2)?.get(g.partner) ?? 0) -
          k2.x;
        const lo = Math.min(0, o);
        schuifSubtree(result, o - lo);
        schuifSubtree(vorige.result, -lo);
        vorige.result = {
          width: Math.max(vorige.result.width, o + result.width) - lo,
          nodes: [...vorige.result.nodes, ...result.nodes],
          links: [...vorige.result.links, ...result.links],
        };
        vorige.wortels.push(k2);
        continue;
      }
      if (result.width > 0) {
        childEntries.push({
          result,
          groupIdx,
          partnerId: group.partnerId,
          isHalfSibling: firstNonEmptyGroupSeen && multiplePartners,
          wortels: [result.nodes[0]],
        });
      }
    }
    if (group.children.length > 0) firstNonEmptyGroupSeen = true;
    groupIdx++;
  }

  // Linkerrand van elk kind-subtree (start) en de plek van de ankerkaart.
  const childDims = getNodeDims(generation + 1);
  const start: number[] = [];
  let subtreeWidth: number;
  let ankerX: number;
  if (planRand) {
    // #1793 — elk gezin aan de kant van het eigen paar: de gezinnen van de partners
    // rechts naar rechts (binnen naar buiten), die van links naar links, kinderen
    // zonder partner ernaast midden onder het anker. Het anker staat zo boven de
    // naad tussen de linker en de rechter gezinnen; geen lijnen die elkaar kruisen.
    const groepGap = childDims.hGap + PARTNER_GROUP_GAP;
    const groepen = new Map<number, number[]>();
    childEntries.forEach((e, i) =>
      groepen.set(e.groupIdx, [...(groepen.get(e.groupIdx) ?? []), i])
    );
    const plek = (gi: number) => plan!.get(partnerGroups[gi].partnerId ?? '') ?? 0;
    const gis = [...groepen.keys()];
    const midden = gis.filter((gi) => plek(gi) === 0);
    const rechts = gis.filter((gi) => plek(gi) > 0).sort((a, b) => plek(a) - plek(b));
    const links_ = gis.filter((gi) => plek(gi) < 0).sort((a, b) => plek(a) - plek(b));
    const breedte = (gs: number[]) =>
      gs.reduce(
        (som, gi, k) =>
          som +
          (k ? groepGap : 0) +
          groepen
            .get(gi)!
            .reduce((t, i, j) => t + (j ? childDims.hGap : 0) + childEntries[i].result.width, 0),
        0
      );
    const leg = (gs: number[], x: number) => {
      for (const gi of gs) {
        for (const i of groepen.get(gi)!) {
          start[i] = x;
          x += childEntries[i].result.width + childDims.hGap;
        }
        x += PARTNER_GROUP_GAP;
      }
    };
    if (gedeeld) {
      // #1807 ronde 6 — de gezinnen met de gedeelde partner tegen de naad onder hem; de
      // rest daarachter. Aan de andere kant van hem begint het gezin van de broer/zus.
      const isHij = (gi: number) => partnerGroups[gi].partnerId === gedeeld.partner;
      const anderen = gis
        .filter((gi) => !isHij(gi) && plek(gi) !== 0)
        .sort((a, b) => plek(a) - plek(b));
      const mid = gis.filter((gi) => !isHij(gi) && plek(gi) === 0);
      const reeks =
        gedeeld.zijde > 0
          ? [...anderen, ...mid, ...gis.filter(isHij)]
          : [...gis.filter(isHij), ...mid, ...anderen];
      const naad = gedeeld.zijde * (unit - groepGap / 2);
      leg(reeks, gedeeld.zijde > 0 ? naad - breedte(reeks) : naad);
    } else {
      const mb = breedte(midden);
      const naad = mb > 0 ? mb / 2 + groepGap : groepGap / 2;
      leg(midden, -mb / 2);
      leg(rechts, naad);
      leg(links_, -naad - breedte(links_));
    }
    let [min, max] = planRand;
    childEntries.forEach((e, i) => {
      min = Math.min(min, start[i]);
      max = Math.max(max, start[i] + e.result.width);
    });
    for (let i = 0; i < start.length; i++) start[i] -= min;
    subtreeWidth = max - min;
    ankerX = -min;
  } else {
    // Calculate total width from child subtrees (use child generation H_GAP)
    let totalChildWidth = 0;
    for (let i = 0; i < childEntries.length; i++) {
      if (i > 0) {
        totalChildWidth += childDims.hGap;
        // Extra gap between partner groups
        if (childEntries[i].groupIdx !== childEntries[i - 1].groupIdx) {
          totalChildWidth += PARTNER_GROUP_GAP;
        }
      }
      totalChildWidth += childEntries[i].result.width;
    }
    // Reserveer minstens de volle paar-breedte zodat het paar binnen de subtree
    // past en gecentreerd kan worden (zie coupleShift/coupleWidth hierboven).
    subtreeWidth = Math.max(coupleWidth, totalChildWidth);
    ankerX = subtreeWidth / 2 - coupleShift;
    // Position child subtrees, centered within subtreeWidth
    let xCursor = (subtreeWidth - totalChildWidth) / 2;
    for (let i = 0; i < childEntries.length; i++) {
      // Extra gap at partner group boundary
      if (i > 0 && childEntries[i].groupIdx !== childEntries[i - 1].groupIdx) {
        xCursor += PARTNER_GROUP_GAP;
      }
      start[i] = xCursor;
      xCursor += childEntries[i].result.width + childDims.hGap;
    }
  }

  // Create the current person's node
  const thisNode: LayoutNode = {
    person,
    x: ankerX,
    // nc#537: zie leaf-node hierboven — negate generation zodat afstammelingen
    // onder de focus-person komen (top-down i.p.v. upside-down).
    y: getGenY(-generation),
    generation,
    isEndOfLine,
    hasMoreData,
    spouses: [],
    nodeWidth: dims.width,
  };
  if (plan) partnerPlan.set(thisNode, plan);

  const allNodes: LayoutNode[] = [thisNode];
  const allLinks: LayoutLink[] = [];

  // #1041 — kinderen hangen aan een family-unit busbar-link (TreeLinks.tsx):
  // anker → dropdown → horizontale busbar → dropdown per kind.
  // #1590 — één gezinslijn PER HUWELIJK (partnergroep), met de ouders in
  // `parentIds`. De definitieve x-posities (vanuit het eigen paar, naar de kaart
  // van elk kind) zet `plaatsGezinslijnen` pas als de partners geplaatst zijn.
  const gezinnen = new Map<number, { partnerId: string | null; xs: number[]; ids: string[] }>();
  let childRowY: number | null = null;

  for (let i = 0; i < childEntries.length; i++) {
    const { result, groupIdx: gi, partnerId } = childEntries[i];
    const xCursor = start[i];

    for (const node of result.nodes) {
      node.x += xCursor;
    }
    for (const link of result.links) {
      link.sourceX += xCursor;
      link.targetX += xCursor;
      // #1041 — family-unit links uit diepere subtrees hebben childXs
      // die ook moeten meeschuiven met xCursor (anders wijzen ze naar
      // de pre-shift posities).
      if (link.childXs) link.childXs = link.childXs.map((x) => x + xCursor);
    }

    for (const childRootNode of childEntries[i].wortels) {
      const gezin = gezinnen.get(gi) ?? { partnerId, xs: [], ids: [] };
      gezin.xs.push(childRootNode.x);
      gezin.ids.push(childRootNode.person.id);
      gezinnen.set(gi, gezin);
      if (childRowY === null) childRowY = childRootNode.y;
    }

    allNodes.push(...result.nodes);
    allLinks.push(...result.links);
  }

  if (childRowY !== null) {
    const busbarY = (thisNode.y + childRowY) / 2;
    // #1590 — elk volgend huwelijk een eigen balk, iets hoger, zodat de gezinnen
    // van twee huwelijken niet als één balk lezen.
    // Binnen de opening blijven, ook bij 3+ huwelijken: de stap schaalt mee.
    const stap =
      gezinnen.size > 1 ? -Math.min(-GEZINSBALK_STAP, GEZINSBALK_RUIMTE / (gezinnen.size - 1)) : 0;
    let volgnr = 0;
    for (const gezin of gezinnen.values()) {
      const eigenStap = volgnr++ * stap;
      const eigenBalkY = busbarY + eigenStap;
      allLinks.push({
        sourceId: thisNode.person.id,
        targetId: gezin.ids[0],
        sourceX: planRand ? ankerX : subtreeWidth / 2, // definitief: plaatsGezinslijnen
        sourceY: thisNode.y,
        targetX: gezin.xs[0],
        targetY: childRowY,
        busbarY: eigenBalkY,
        busbarStap: eigenStap,
        childXs: gezin.xs,
        childIds: gezin.ids,
        parentIds: gezin.partnerId ? [thisNode.person.id, gezin.partnerId] : [thisNode.person.id],
        childPediTypes: relationLines
          ? gezin.ids.map((cid) => pediTypeFor(thisNode.person.id, cid, relIndex, true))
          : undefined,
        type: 'family-unit',
      });
    }
  }

  return { width: subtreeWidth, nodes: allNodes, links: allLinks };
}

// ─── Spouse Attachment ──────────────────────────────────────────────────────

/**
 * Attach spouses to each node and generate spousal links.
 * Spouses are sorted by marriage date and include relationship status.
 * For spouses that are NOT already main nodes, a separate LayoutNode is
 * created so they render as visible cards in the tree.
 *
 * Nextcloud #562 — onderscheid primaire vs extra partners:
 * - Primaire partner = degene met wie deze persoon een kind heeft dat ook
 *   in de boom staat (bloedlijn). Altijd zichtbaar, ongeacht de toggle.
 * - Extra partner = bijv. eerdere echtgenote zonder gezamenlijke kinderen
 *   in de boom (Saskia-scenario). Alleen zichtbaar als
 *   `showExtraPartners=true`. Spousal-links tussen main-nodes (ouder-paren
 *   die beide al in de boom staan) blijven áltijd zichtbaar.
 */
function attachSpouses(
  nodes: LayoutNode[],
  relIndex: RelIndex,
  personMap: Map<string, Person>,
  focusPersonId: string,
  events: GenealogyEvent[],
  rootId?: string,
  collapsedPartnerNodes?: Set<string>,
  viewMode?: TreeViewMode,
  showExtraPartners: boolean = true,
  relationLines: boolean = false,
  partnerGap?: number,
  wisselExtra: number = 0
): { spousalLinks: LayoutLink[]; spouseNodes: LayoutNode[] } {
  const spousalLinks: LayoutLink[] = [];
  const spouseNodes: LayoutNode[] = [];
  const mainNodeIds = new Set(nodes.map((n) => n.person.id));
  const createdSpouseIds = new Set<string>();
  const linkedPairs = new Set<string>(); // prevent duplicate spousal links (A→B and B→A)

  // In PATRILINEAL/MATRILINEAL view, show only the co-parent (the other parent of the
  // child in the ancestral line) next to each person — not all partners.
  // In other views, only show extra spouse nodes for the focus person + ouders met
  // kinderen in de boom (= primaire partners van de bloedlijn-takken).
  const isLineView = viewMode === 'PATRILINEAL' || viewMode === 'MATRILINEAL';
  const expandedPersonIds = new Set<string>();
  const lineCoParentIds = new Set<string>(); // whitelist: only these spouse IDs get shown in line view

  // #562 — bouw mapping: persoon → set van zijn co-parents waarmee hij/zij een
  // kind heeft dat ook in de boom staat. Wordt later gebruikt om primaire
  // partners te classificeren ongeacht de showExtraPartners toggle.
  const primaryCoParentByPerson = new Map<string, Set<string>>();
  for (const node of nodes) {
    const childRels = relIndex.byParent.get(node.person.id) ?? [];
    for (const childRel of childRels) {
      const childId = childRel.personB;
      if (!mainNodeIds.has(childId)) continue;
      // Kind staat in de boom — zoek de andere ouder = bloedlijn-partner
      const parentsOfChild = relIndex.byChild.get(childId) ?? [];
      for (const parentRel of parentsOfChild) {
        if (parentRel.personA !== node.person.id) {
          let set = primaryCoParentByPerson.get(node.person.id);
          if (!set) {
            set = new Set();
            primaryCoParentByPerson.set(node.person.id, set);
          }
          set.add(parentRel.personA);
        }
      }
    }
  }

  if (isLineView && nodes.length >= 2) {
    // Build co-parent whitelist: for each ancestor node, find the child that is also
    // in the line, then find the OTHER parent of that child (= the co-parent / spouse).
    const nodeIds = new Set(nodes.map((n) => n.person.id));
    for (const node of nodes) {
      // Find children of this node that are also in the line
      const childRels = relIndex.byParent.get(node.person.id) ?? [];
      for (const childRel of childRels) {
        const childId = childRel.personB;
        if (!nodeIds.has(childId)) continue;
        // Found a child in the line — now find the OTHER parent
        const parentsOfChild = relIndex.byChild.get(childId) ?? [];
        for (const parentRel of parentsOfChild) {
          if (parentRel.personA !== node.person.id) {
            const coParent = personMap.get(parentRel.personA);
            if (coParent && !nodeIds.has(coParent.id)) {
              lineCoParentIds.add(coParent.id);
              expandedPersonIds.add(node.person.id);
            }
          }
        }
      }
    }
  } else if (viewMode === 'DESCENDANTS') {
    // In DESCENDANTS view: expand alle personen zodat partners bij elke
    // afstammeling getoond worden (genealogische conventie, nc#537).
    for (const node of nodes) {
      expandedPersonIds.add(node.person.id);
    }
  } else {
    // PEDIGREE / COMPLETE / default — Nextcloud #562:
    // Niet alleen de proband, maar elke node met een kind in de boom krijgt
    // zijn primaire partner getoond (anders zien we de bloedlijn-partner niet
    // bij voorouders/kinderen — Emma's bug).
    expandedPersonIds.add(focusPersonId);
    for (const personId of primaryCoParentByPerson.keys()) {
      expandedPersonIds.add(personId);
    }
  }

  for (const node of nodes) {
    const allSortedSpouses = getSpousesSorted(node.person.id, relIndex, personMap, events);

    // Store full spouse list on node (for collapse button count)
    node.spouses = allSortedSpouses.map((s) => s.spouse);
    // #1806 — de inklapknop alleen als inklappen iets verbergt: een kinderloze partner.
    node.inklapbaar =
      allSortedSpouses.length >= 2 &&
      allSortedSpouses.some((s) => !gezamenlijkKind(node.person.id, s.spouse.id, relIndex));

    // Ingeklapt: alleen de partners met een gezamenlijk kind (#1806)
    const sortedSpouses = zichtbarePartners(
      node.person.id,
      allSortedSpouses,
      collapsedPartnerNodes,
      relIndex
    );

    // In line view, filter to only co-parents
    let filteredSpouses = isLineView
      ? sortedSpouses.filter(
          (s) => lineCoParentIds.has(s.spouse.id) || mainNodeIds.has(s.spouse.id)
        )
      : sortedSpouses;

    // #562 — Nextcloud: in non-line/non-descendants views filter we de "extra"
    // partners eruit als de toggle uit staat. Primaire partners (= co-parents
    // van een kind in de boom) blijven altijd zichtbaar, ongeacht de toggle —
    // anders verdwijnen huwelijkslijnen naar de bloedlijn.
    if (!isLineView && viewMode !== 'DESCENDANTS' && !showExtraPartners) {
      const primarySet = primaryCoParentByPerson.get(node.person.id);
      filteredSpouses = filteredSpouses.filter(
        (s) =>
          mainNodeIds.has(s.spouse.id) || // partner is zelf een main-node (ouder-paar)
          (primarySet?.has(s.spouse.id) ?? false) // partner is co-parent van een kind in de boom
      );
    }

    // Count how many non-main-node spouses this person has. #1808 — in Nakomelingen krijgt
    // een partner die zelf in de boom staat (huwelijk tussen verwanten) een verwijskaart
    // naast deze kaart; die telt als gewone partner (layoutDescendants, `naast.verwijs`).
    const verwijs = viewMode === 'DESCENDANTS';
    const nonMainSpouses = filteredSpouses.filter((s) => verwijs || !mainNodeIds.has(s.spouse.id));
    const isExpanded = expandedPersonIds.has(node.person.id);

    // Hidden extra-spouse-badge: aantal partners dat verborgen is ten opzichte
    // van wat in `allSortedSpouses` zit. Bij showExtraPartners=false kunnen dat
    // er meer zijn dan alleen "niet-main" partners — daarom hier op allSorted-
    // basis rekenen (#562).
    const visibleSpouseIds = new Set(filteredSpouses.map((s) => s.spouse.id));
    const hiddenCount = allSortedSpouses.filter(
      (s) => (verwijs || !mainNodeIds.has(s.spouse.id)) && !visibleSpouseIds.has(s.spouse.id)
    ).length;
    if (!isExpanded && nonMainSpouses.length > 0) {
      node.hiddenSpouseCount = nonMainSpouses.length;
    } else if (hiddenCount > 0) {
      node.hiddenSpouseCount = hiddenCount;
    }

    // De partners die naast deze kaart komen. #1807 — niet wie al een kaart heeft naast een
    // eerdere kaart (een man die twee zussen trouwde); layoutDescendants reserveert voor hem
    // dan ook geen plek (naast.geplaatst, zelfde volgorde).
    const naastSpouses = nonMainSpouses.filter((s) => !createdSpouseIds.has(s.spouse.id));
    const naarEerdereKaart: LayoutLink[] = [];

    // Build spouseRelationships info
    const spouseInfos: SpouseInfo[] = [];
    let nonMainIdx = 0;
    // #1793 — twee of meer partners naast de kaart: het plan van layoutDescendants (de
    // kaart blijft staan, de partners afwisselend rechts en links). De hoofdpersoon van een
    // stamboom zonder getoonde kinderen krijgt hetzelfde beeld (rij 0 heeft geen buren).
    // Voorouders (Sosa ≥ 2) houden hun extra partners aan de buitenkant (NC #842).
    let plan = partnerPlan.get(node);
    if (
      !plan &&
      isExpanded &&
      viewMode !== 'DESCENDANTS' &&
      node.person.id === focusPersonId &&
      node.generation === 0 &&
      naastSpouses.length >= 2
    ) {
      const d = getNodeDims(0);
      const wisseltHier = wisseltEerstePartner(
        node.person,
        naastSpouses.map((s) => s.spouse)
      );
      plan = partnerPlekken(
        naastSpouses.map((s) => s.spouse.id),
        wisseltHier,
        d.width + (partnerGap ?? d.spouseGap),
        wisseltHier || node.inklapbaar ? wisselExtra : 0
      );
    }
    // Gender-aware: de vrouw schuift op als haar eerste partner naast haar een man is
    // (niet in MATRILINEAL: daar is de vrouw de lijnpersoon en blijft ze staan).
    const wisselt =
      !plan &&
      viewMode !== 'MATRILINEAL' &&
      wisseltEerstePartner(
        node.person,
        naastSpouses.map((s) => s.spouse)
      );

    // Blocker 2 — bereken marriageOrder voor deze persoon. Sorteer alle spousal
    // relations chronologisch op marriage-date; volgnummer 1-based. Onbekende
    // datums komen laatst (zelfde semantiek als getSpousesSorted).
    // NB: marriageOrder is symmetrisch per persoon — voor een paar (A,B) kan
    // het nummer per kant verschillen (A's 2e huwelijk kan B's 1e zijn). We
    // gebruiken het volgnummer vanuit `node.person` zodat de remarriage-pill
    // op de relatieve volgorde voor de bekeken persoon correct is.
    const allSpousalRels = relIndex.spousal.get(node.person.id) ?? [];
    const allSpouseOrderEntries = allSpousalRels.map((spousalRel) => {
      const marriageEvent = events.find(
        (e) =>
          e.type === 'marriage' &&
          e.participants?.some((p) => p.personId === spousalRel.personA) &&
          e.participants?.some((p) => p.personId === spousalRel.personB)
      );
      const year = marriageEvent?.date?.match(/\d{4}/)?.[0];
      return {
        otherId: spousalRel.personA === node.person.id ? spousalRel.personB : spousalRel.personA,
        sortKey: year ? parseInt(year, 10) : 9999,
      };
    });
    allSpouseOrderEntries.sort((a, b) => a.sortKey - b.sortKey);
    const marriageOrderByOtherId = new Map<string, number>();
    allSpouseOrderEntries.forEach((entry, idx) => {
      marriageOrderByOtherId.set(entry.otherId, idx + 1);
    });

    for (let i = 0; i < filteredSpouses.length; i++) {
      const { spouse, status, marriageDate, marriagePlace, rel } = filteredSpouses[i];

      // Re-haal divorce-event op met events-data zodat divorceDate correct is.
      const divorceEvent = events.find(
        (e) =>
          e.type === 'divorce' &&
          e.participants?.some((p) => p.personId === rel.personA) &&
          e.participants?.some((p) => p.personId === rel.personB)
      );
      const divorceDate = divorceEvent?.date;
      spouseInfos.push({
        spouseId: spouse.id,
        marriageDate,
        divorceDate,
        status,
      });

      // Blocker 2 — childrenCount: aantal gedeelde kinderen tussen dit paar.
      // Intersection van byParent[node] en byParent[spouse], geteld op unieke
      // child-id. Duplicaten worden via Set ontdubbeld.
      const myChildIds = new Set(
        (relIndex.byParent.get(node.person.id) ?? []).map((r) => r.personB)
      );
      const sharedChildren = (relIndex.byParent.get(spouse.id) ?? []).filter((r) =>
        myChildIds.has(r.personB)
      );
      const uniqueSharedChildIds = new Set(sharedChildren.map((r) => r.personB));
      const childrenCount = uniqueSharedChildIds.size;

      const marriageOrder = marriageOrderByOtherId.get(spouse.id);

      const spDims = getNodeDims(Math.abs(node.generation));

      // If the spouse is also a main node, use its actual position for the link
      const spouseMainNode =
        !verwijs && mainNodeIds.has(spouse.id)
          ? nodes.find((n) => n.person.id === spouse.id)
          : undefined;

      if (spouseMainNode) {
        // Both partners are main nodes (placed by layoutAncestors).
        // Those layout functions already handle gender-aware ordering (M left, F right).
        // Do NOT swap positions here — swapping root nodes without their subtrees
        // causes crossed relationship lines.

        // Deduplicate: when both partners are main nodes, each iterates over the
        // other via the bidirectional spousal index — only create the link once.
        const pairKey = [node.person.id, spouse.id].sort().join('-');
        if (linkedPairs.has(pairKey)) continue;
        linkedPairs.add(pairKey);

        // Link goes to the main node's layout-assigned position
        spousalLinks.push({
          sourceId: node.person.id,
          targetId: spouse.id,
          sourceX: node.x,
          sourceY: node.y,
          targetX: spouseMainNode.x,
          targetY: spouseMainNode.y,
          type: 'spousal',
          relationshipStatus: status,
          marriageDate,
          marriagePlace,
          // Blocker 2 — koppel divorce-event + multi-marriage + kinderen-telling
          // door zodat ExportDialog `divorceDate`/`marriageOrder`/`childrenCount`
          // velden kan tonen (relationFieldRegistry.ts).
          divorceDate,
          marriageOrder: marriageOrder && marriageOrder >= 2 ? marriageOrder : undefined,
          childrenCount: childrenCount > 0 ? childrenCount : undefined,
          isPrimaryUnion: primaryCoParentByPerson.get(node.person.id)?.has(spouse.id) ?? false,
          relationType: relationLines ? resolvePartnerLineType(rel.relationSubtype) : undefined,
        });
      } else if (isExpanded) {
        // Non-main spouse AND this person is expanded (focus/proband) → show the node
        // Gender-aware positioning: male always left, female always right.
        // Exception: in MATRILINEAL view the female is the lineage person — she stays
        // in position and the male co-parent goes to the right (no swap).
        // #1807 — staat deze partner al naast een eerdere kaart, dan geen tweede kaart en
        // geen plek: de lijn loopt naar zijn echte kaart.
        const eerder = createdSpouseIds.has(spouse.id)
          ? spouseNodes.find((s) => s.person.id === spouse.id)
          : undefined;
        // #1606 — wisseltEerstePartner is ook de beslisser voor de ruimte (layoutDescendants).
        const swapPositions = !eerder && nonMainIdx === 0 && wisselt;

        // Place spouse next to main node with tight spouse gap (not sibling hGap).
        let spouseX: number;
        const geplandX = plan?.get(spouse.id);
        if (eerder) {
          spouseX = eerder.x;
        } else if (geplandX !== undefined) {
          spouseX = node.x + geplandX;
        } else if (swapPositions) {
          // Male spouse takes the main node's position (left), female node shifts right
          spouseX = node.x;
          node.x = node.x + spDims.width + (partnerGap ?? spDims.spouseGap);
        } else {
          spouseX = node.x + (nonMainIdx + 1) * (spDims.width + (partnerGap ?? spDims.spouseGap));
        }
        const spouseY = eerder?.y ?? node.y;
        // #1808 — verwijskaart: een kopie met een eigen id (alle passen werken op person.id),
        // bij elk van beide verwanten één.
        const verwijsId = mainNodeIds.has(spouse.id)
          ? verwijsKaartId(node.person.id, spouse.id)
          : undefined;

        (eerder ? naarEerdereKaart : spousalLinks).push({
          sourceId: node.person.id,
          targetId: verwijsId ?? spouse.id,
          sourceX: node.x,
          sourceY: node.y,
          targetX: spouseX,
          targetY: spouseY,
          type: 'spousal',
          relationshipStatus: status,
          marriageDate,
          marriagePlace,
          // Blocker 2 — zie main-node-branch hierboven voor toelichting.
          divorceDate,
          marriageOrder: marriageOrder && marriageOrder >= 2 ? marriageOrder : undefined,
          childrenCount: childrenCount > 0 ? childrenCount : undefined,
          isPrimaryUnion: primaryCoParentByPerson.get(node.person.id)?.has(spouse.id) ?? false,
          relationType: relationLines ? resolvePartnerLineType(rel.relationSubtype) : undefined,
        });

        // Create a visible LayoutNode for this non-main spouse
        if (verwijsId) {
          spouseNodes.push({
            person: { ...spouse, id: verwijsId },
            x: spouseX,
            y: spouseY,
            generation: node.generation,
            isEndOfLine: false,
            hasMoreData: false,
            spouses: [],
            nodeWidth: spDims.width,
            hasHiddenParents: false,
            hasHiddenChildren: false,
            isSpouseNode: true,
            verwijstNaar: spouse.id,
          });
        } else if (!createdSpouseIds.has(spouse.id)) {
          createdSpouseIds.add(spouse.id);

          spouseNodes.push({
            person: spouse,
            x: spouseX,
            y: spouseY,
            generation: node.generation,
            sosaNumber: undefined,
            isEndOfLine: false,
            hasMoreData: false,
            spouses: [],
            nodeWidth: spDims.width,
            ancestorCount: undefined,
            hasHiddenParents: hasParentsInDataset(spouse.id, relIndex, personMap),
            hasHiddenChildren: hasChildrenInDataset(spouse.id, relIndex, personMap),
            isSpouseNode: true,
          });
        }

        if (!eerder) nonMainIdx++;
      }
      // else: non-main spouse but person is NOT expanded → skip node creation (hidden, shown as badge)
    }
    // Pas na de lus: een wissel verderop kan deze kaart nog verplaatst hebben.
    for (const l of naarEerdereKaart) {
      l.sourceX = node.x;
      spousalLinks.push(l);
    }

    node.spouseRelationships = spouseInfos;
  }

  return { spousalLinks, spouseNodes };
}

// ─── Center on Proband ──────────────────────────────────────────────────────

/**
 * Shift all nodes and links so that the proband (focusPersonId) sits at (0, 0).
 */
function centerOnProband(nodes: LayoutNode[], links: LayoutLink[], focusPersonId: string): void {
  const proband = nodes.find((n) => n.person.id === focusPersonId);
  if (!proband) return;

  const offsetX = proband.x;
  const offsetY = proband.y;

  for (const node of nodes) {
    node.x -= offsetX;
    node.y -= offsetY;
    if (node.placeholderX !== undefined) node.placeholderX -= offsetX;
  }

  for (const link of links) {
    link.sourceX -= offsetX;
    link.sourceY -= offsetY;
    link.targetX -= offsetX;
    link.targetY -= offsetY;
    // FAM-layout (#534) extensies meeschuiven
    if (link.busbarY !== undefined) link.busbarY -= offsetY;
    if (link.childXs !== undefined) {
      link.childXs = link.childXs.map((x) => x - offsetX);
    }
  }
}

// ─── Main Hook ──────────────────────────────────────────────────────────────

/**
 * useTreeLayout — Custom compact pedigree layout hook.
 *
 * Computes a space-efficient tree layout that only allocates space for
 * nodes that actually exist, unlike d3.tree() which reserves slots for
 * all possible positions in a perfect binary tree.
 *
 * @param people         - All persons in the project
 * @param relationships  - All relationships in the project
 * @param focusPersonId  - The proband / focus person (positioned at 0,0)
 * @param viewMode       - PEDIGREE | PATRILINEAL | MATRILINEAL | DESCENDANTS
 * @param depthLimit     - Maximum number of generations to traverse
 * @param isEditMode     - Whether edit mode is active
 * @param rootId         - The project's proband ID (for stable SOSA numbering)
 * @returns              - { nodes, links } ready for rendering
 */
/**
 * Pure (niet-hook) layout-functie die de oorspronkelijke `useTreeLayout`
 * logica bevat. Geëxporteerd zodat `useTreeLayoutV1` (memo-wrapper) en
 * `useTreeLayoutV2` (variable-size-aware variant) dezelfde basis kunnen
 * gebruiken.
 *
 * Geen `useMemo` hier — callers wrappen zelf.
 */
export function computeTreeLayoutPureV1(
  people: Person[],
  relationships: Relationship[],
  focusPersonId: string,
  viewMode: TreeViewMode,
  depthLimit: number,
  isEditMode: boolean = false,
  rootId?: string,
  showSpouses: boolean = true,
  collapsedPartnerNodes?: Set<string>,
  descendantDepth: number = 0,
  events: GenealogyEvent[] = [],
  partnerFootprint: boolean = false,
  relationLines: boolean = false,
  editPlaceholders: boolean = false,
  implex: boolean = false,
  partnerGap?: number,
  // #1606 ronde 2 — alleen computeScreenTreeLayout geeft SCHERM_WISSEL_EXTRA mee.
  wisselExtra: number = 0,
  // #1957 — archiefvoorstellen (alleen het scherm).
  voorstelPlekken?: LaagPlek[]
): {
  nodes: LayoutNode[];
  links: LayoutLink[];
  dataMaxGeneration: number;
} {
  // partnerGap: alleen DESCENDANTS (parenteel-kaarten); andere weergaven houden spouseGap.
  const descPartnerGap = viewMode === 'DESCENDANTS' ? partnerGap : undefined;
  // #1089 — placeholders worden alleen gegenereerd in edit-modus (de orchestrator
  // levert `editPlaceholders = flag-on && isEditMode`). `isEditMode` zelf raakt de
  // layout verder niet; de boolean hieronder is de echte gate.
  void isEditMode;
  // Compact mode threshold per view type:
  // - Line views (PATRILINEAL/MATRILINEAL) + DESCENDANTS: never compact (Infinity).
  //   #1009: nakomelingen tonen alleen afstammelingen (minder nodes) → geen
  //   compaction nodig. Voorkomt ook export-overlap: compacte 111px-spacing
  //   botste met de 180px render-breedte (applyVariableSizeAwareSpacing herstelt
  //   alleen PEDIGREE).
  // - Other views: compact from gen 4 (default)
  _compactGen =
    viewMode === 'PATRILINEAL' || viewMode === 'MATRILINEAL' || viewMode === 'DESCENDANTS'
      ? Infinity
      : COMPACT_GEN;

  // Early exit: no focus person or empty data
  if (!focusPersonId || people.length === 0) {
    return { nodes: [], links: [], dataMaxGeneration: 0 };
  }

  // Build lookup maps
  const personMap = new Map<string, Person>();
  for (const person of people) {
    personMap.set(person.id, person);
  }

  if (!personMap.has(focusPersonId)) {
    return { nodes: [], links: [], dataMaxGeneration: 0 };
  }

  // #1957 plan 5 — archiefkinderen in de nakomelingen: als kind van hun paar in de indeling.
  const nakomKinderen =
    viewMode === 'DESCENDANTS'
      ? nakomKindData((voorstelPlekken ?? []).filter(isNakomKindPlek), personMap)
      : { personen: [], relaties: [] };
  for (const p of nakomKinderen.personen) personMap.set(p.id, p);
  const relIndex = buildRelIndex(
    nakomKinderen.relaties.length ? [...relationships, ...nakomKinderen.relaties] : relationships
  );

  let result: SubtreeResult;

  // #1009 — de nakomelingen-VIEW wortelt op de gekozen focus-persoon: je ziet de
  // nakomelingen vanaf wie je selecteert. De d'Aboville-NUMMERING telt vanaf
  // diezelfde focus (= de wortel/stamouder van de getoonde nakomelingenstaat):
  // focus = 1, kinderen 1.1/1.2, kleinkinderen 1.1.1… Dit is de genealogische
  // standaard voor een nakomelingenstaat (Sosa is een voorouder-systeem en is
  // hier niet van toepassing).
  if (viewMode === 'DESCENDANTS') {
    // Top-down: focus person at top, descendants below
    result = layoutDescendants(
      focusPersonId,
      0,
      depthLimit,
      personMap,
      relIndex,
      new Set<string>(),
      partnerFootprint,
      relationLines,
      events,
      descPartnerGap,
      {
        mainIds: nakomelingIds(focusPersonId, depthLimit, personMap, relIndex, events),
        ingeklapt: collapsedPartnerNodes,
        extra: wisselExtra,
        ookBladen: true,
        verwijs: true,
      }
    );
  } else if (viewMode === 'PEDIGREE') {
    // Pedigree: Sosa-based direct positional layout (correct centering guaranteed)
    const sosaMap = buildSosaMap(focusPersonId, personMap, relIndex);
    const implexSlots = implex
      ? buildSosaSlots(focusPersonId, personMap, relIndex, depthLimit)
      : undefined;
    // #1964 — voorstellen in het rooster: de boom maakt er een generatie voor (Twan 03-11). Alleen
    // proband-relatief, net als `plaatsVoorstellen`; de lijnweergaven gebruiken dat pad nog.
    const voorstelSosa =
      voorstelPlekken?.length && (rootId || focusPersonId) === focusPersonId
        ? voorstelRooster(
            voorstelPlekken.filter((p): p is VoorstelPlek => !isNakomKindPlek(p)),
            implexSlots
              ? implexSlots.sosaToId
              : new Map(
                  [...sosaMap]
                    .filter(([, s]) => Math.floor(Math.log2(s)) <= depthLimit)
                    .map(([pid, s]) => [s, pid])
                ),
            personMap
          )
        : undefined;
    result = layoutPedigree(
      focusPersonId,
      depthLimit,
      personMap,
      relIndex,
      sosaMap,
      relationLines,
      implexSlots,
      voorstelSosa
    );
    for (const n of result.nodes) {
      if (!voorstelSosa?.has(n.sosaNumber ?? -1) || !n.person.id.startsWith('__voorstel__'))
        continue;
      const kind = result.nodes.find((k) => k.sosaNumber === Math.floor(n.sosaNumber! / 2));
      n.isVoorstel = true;
      n.voorstelSleutel = n.person.id.slice('__voorstel__'.length);
      n.isEndOfLine = false;
      n.hasMoreData = false;
      n.placeholderParentRole = n.sosaNumber! % 2 ? 'mother' : 'father';
      n.placeholderForChildId = kind?.person.id;
    }

    // #557: optionele descendants onder de focus-persoon (hourglass-style)
    appendDescendantsToLayout(
      result,
      focusPersonId,
      descendantDepth,
      personMap,
      relIndex,
      events,
      relationLines,
      collapsedPartnerNodes,
      wisselExtra
    );
  } else {
    // Line views (PATRILINEAL/MATRILINEAL): recursive single-parent chain
    result = layoutAncestors(
      focusPersonId,
      0,
      depthLimit,
      personMap,
      relIndex,
      viewMode,
      new Set<string>(),
      relationLines
    );

    // 2 generations of descendants below the focus person (genealogische
    // conventie voor stamreeks-views; eigen vaste diepte, los van #557)
    appendDescendantsToLayout(
      result,
      focusPersonId,
      2,
      personMap,
      relIndex,
      events,
      relationLines,
      collapsedPartnerNodes,
      wisselExtra
    );
  }

  if (result.nodes.length === 0) {
    return { nodes: [], links: [], dataMaxGeneration: 0 };
  }

  // Compute the maximum ancestor depth from full data (follows longest path, handles pedigree collapse)
  // #1964 — een voorstel (één generatie boven de grens) is geen data: de schuif gaat er niet verder door.
  let dataMaxGeneration = Math.max(
    ...result.nodes.filter((n) => !n.isVoorstel).map((n) => Math.abs(n.generation)),
    0
  );
  if (viewMode === 'DESCENDANTS') {
    // DFS over full data — slider must not be bounded by current depthLimit slice
    dataMaxGeneration = Math.max(
      dataMaxGeneration,
      computeMaxDescendantDepth(focusPersonId, personMap, relIndex)
    );
  } else {
    const sosaSource = rootId || focusPersonId;

    // Max depth via DFS tracking longest path per person
    dataMaxGeneration = Math.max(
      dataMaxGeneration,
      computeMaxAncestorDepth(sosaSource, personMap, relIndex)
    );

    // Assign Sosa numbers — always from the project proband (rootId), not focusPersonId.
    // Implex-uitzondering: bij `implex && PEDIGREE` is node.sosaNumber al per-slot gezet in
    // layoutPedigree (instances delen person.id maar hebben distinct sosa); de person.id-
    // keyed map zou ze allemaal naar één nummer overschrijven → overslaan.
    if (!(implex && viewMode === 'PEDIGREE')) {
      const sosaMap = buildSosaMap(sosaSource, personMap, relIndex);
      for (const node of result.nodes) {
        const sosa = sosaMap.get(node.person.id);
        if (sosa !== undefined) node.sosaNumber = sosa;
      }
    }
  }

  // Assign ancestor counts for nodes at the depth limit
  if (viewMode !== 'DESCENDANTS') {
    for (const node of result.nodes) {
      if (node.hasMoreData) {
        const visited = new Set<string>();
        node.ancestorCount = countAncestorsAbove(node.person.id, relIndex, personMap, visited);
      }
    }
  }

  // Attach spouses and generate spousal links + visible spouse nodes.
  // #562 — attachSpouses wordt nu áltijd aangeroepen. De `showSpouses` toggle
  // bepaalt of EXTRA partners (zonder kinderen in de boom) zichtbaar worden;
  // primaire bloedlijn-partners blijven altijd zichtbaar zodat huwelijks-
  // lijnen niet verdwijnen bij toggle UIT.
  const { spousalLinks, spouseNodes } = attachSpouses(
    result.nodes,
    relIndex,
    personMap,
    focusPersonId,
    events,
    rootId,
    collapsedPartnerNodes,
    viewMode,
    showSpouses,
    relationLines,
    descPartnerGap,
    wisselExtra
  );
  const allNodes = [...result.nodes, ...spouseNodes];
  const allLinks = [...result.links, ...spousalLinks];

  // #1957 plan 5 — de archiefkinderen zijn voorstelknopen; hun ouders houden het einde-van-de-lijn
  // van de echte data (een archiefkind is nog geen kind in de boom).
  if (nakomKinderen.personen.length) {
    for (const n of allNodes) {
      if (isNakomKindId(n.person.id)) {
        n.isVoorstel = true;
        n.voorstelSleutel = n.person.id.slice(NAKOM_KIND_PREFIX.length);
        n.isEndOfLine = false;
        continue;
      }
      const kinderen = relIndex.byParent.get(n.person.id) ?? [];
      if (kinderen.length && kinderen.every((r) => isNakomKindId(r.personB))) {
        n.isEndOfLine = true;
        n.hasMoreData = false;
      }
    }
  }

  // NC #634 punt 4 — partners in de lijn-views kregen geen Sosa-nummer.
  //
  // De toekenning hierboven loopt over `result.nodes`, maar `attachSpouses` maakt
  // de partner-nodes pas hier aan: ze bestaan simpelweg nog niet wanneer de
  // nummers worden uitgedeeld. In PEDIGREE valt dat niet op omdat
  // getFilteredParents daar béide ouders als echte parent-node teruggeeft; in
  // MATRILINEAL/PATRILINEAL komt alleen de lijn-ouder binnen en kan de partner
  // uitsluitend via attachSpouses verschijnen. Emma's moederreeks liet dat zien:
  // de lijn had 1-3-7-15-31, de partners niets — terwijl dat wél voorouders zijn
  // (de vader is 2, de grootvader van moederszijde 6).
  //
  // Alleen spouse-nodes worden hier alsnog genummerd; aan de bestaande toekenning
  // verandert niets. Nodes die al een nummer hebben blijven ongemoeid, en de
  // implex-uitzondering (per-slot sosa bij PEDIGREE) wordt niet geraakt omdat
  // spouse-nodes daar geen aparte slot-instanties zijn.
  if (viewMode !== 'DESCENDANTS' && spouseNodes.length > 0) {
    const sosaSourceForSpouses = rootId || focusPersonId;
    const spouseSosaMap = buildSosaMap(sosaSourceForSpouses, personMap, relIndex);
    for (const node of spouseNodes) {
      if (node.sosaNumber != null) continue;
      const sosa = spouseSosaMap.get(node.person.id);
      if (sosa !== undefined) node.sosaNumber = sosa;
    }
  }

  // #1089 — edit-mode "missing parent" placeholders. Only for ancestor views and
  // only when the resolved gate is on. Generated from the REAL (non-spouse) nodes
  // that already have their final x/y, then appended to `allNodes` so they flow
  // through the same per-generation overlap-resolution pass below (first-class).
  const isAncestorView =
    viewMode === 'PEDIGREE' || viewMode === 'PATRILINEAL' || viewMode === 'MATRILINEAL';
  // #1957 — archiefvoorstellen langs hetzelfde pad. Hun Sosa is proband-relatief; de Sosa op de
  // knopen alleen als de boom op de proband staat (bij een andere focus deels focus-relatief).
  const voorstelNodes: LayoutNode[] =
    voorstelPlekken?.length &&
    isAncestorView &&
    viewMode !== 'PEDIGREE' &&
    (rootId || focusPersonId) === focusPersonId
      ? plaatsVoorstellen(
          allNodes,
          voorstelPlekken.filter((p): p is VoorstelPlek => !isNakomKindPlek(p)),
          viewMode
        )
      : [];
  // Een voorstel op de plek van een placeholder wint: daar geen placeholder. #1964 — in de Stamboom staan
  // de voorstellen al in het rooster (`allNodes`); boven een voorstel komt nooit een placeholder.
  const alleVoorstellen = [...voorstelNodes, ...allNodes.filter((n) => n.isVoorstel)];
  const placeholderNodes: LayoutNode[] =
    editPlaceholders && isAncestorView
      ? generateParentPlaceholders(
          allNodes.filter((n) => !n.isVoorstel),
          relIndex,
          personMap,
          viewMode,
          depthLimit
        ).filter(
          (ph) =>
            !alleVoorstellen.some(
              (v) =>
                v.placeholderForChildId === ph.placeholderForChildId &&
                v.placeholderParentRole === ph.placeholderParentRole
            )
        )
      : [];
  const spookknopen = [...placeholderNodes, ...voorstelNodes];
  allNodes.push(...spookknopen);

  // Capture each placeholder's IDEAL (pre-packing) Sosa-slot x. After the
  // overlap-resolution pass below, any node (real or placeholder) may have been
  // shifted to resolve overlap — the pass moves whatever is needed. We compare
  // pre- and post-pass positions and record the adjusted value in `placeholderX`.
  const placeholderIdealX = new Map<string, number>();
  for (const ph of spookknopen) placeholderIdealX.set(ph.person.id, ph.x);

  // Re-sync parent-child link coordinates after attachSpouses may have
  // swapped node positions (gender-aware male-left/female-right swap).
  // Implex-uitzondering: bij PEDIGREE+implex delen duplicaat-nodes person.id, dus een
  // person.id-keyed Map zou hun link-coördinaten samenvouwen (één instance wint) en de
  // andere parent-kind-lijn naar de verkeerde positie zetten. layoutPedigree heeft de
  // link-coördinaten daar al per-slot correct gezet → re-sync overslaan.
  if (!(implex && viewMode === 'PEDIGREE')) {
    const nodeById = new Map(allNodes.map((n) => [n.person.id, n]));
    for (const link of allLinks) {
      if (link.type === 'spousal') continue;
      const src = nodeById.get(link.sourceId);
      const tgt = nodeById.get(link.targetId);
      if (src) {
        link.sourceX = src.x;
        link.sourceY = src.y;
      }
      if (tgt) {
        link.targetX = tgt.x;
        link.targetY = tgt.y;
      }
    }
  }

  // Post-process: resolve overlaps by shifting nodes apart per generation.
  // Skip for PEDIGREE view — the Sosa-based layout guarantees no overlaps
  // among ancestor nodes.
  // #1089 — EXCEPTION: when edit-mode placeholders were injected, run the pass
  // for PEDIGREE too so placeholders that land too close to a real node (or to
  // each other on a couple-row) get packed apart. The pass only shifts when an
  // actual overlap exists, so already-correct real nodes keep their positions.
  if (viewMode !== 'PEDIGREE' || spookknopen.length > 0) {
    // Group by y-position (rounded to avoid floating point issues)
    const yMap = new Map<number, LayoutNode[]>();
    for (const n of allNodes) {
      const yKey = Math.round(n.y);
      if (!yMap.has(yKey)) yMap.set(yKey, []);
      yMap.get(yKey)!.push(n);
    }

    // Build node lookup for efficient link coordinate updates
    const nodeById = new Map<string, LayoutNode>();
    for (const n of allNodes) nodeById.set(n.person.id, n);

    for (const [, rowNodes] of yMap) {
      rowNodes.sort((a, b) => a.x - b.x);
      // Resolve overlaps: shift nodes apart when they overlap.
      let changed = true;
      let maxPasses = 10;
      while (changed && maxPasses-- > 0) {
        changed = false;
        for (let i = 1; i < rowNodes.length; i++) {
          const prev = rowNodes[i - 1];
          const curr = rowNodes[i];
          const prevHalfW = (prev.nodeWidth || NODE_WIDTH) / 2;
          const currHalfW = (curr.nodeWidth || NODE_WIDTH) / 2;
          // Couple-aware gap: spouses stay close, siblings get more space
          // #1808 — een verwijskaart telt als de persoon naar wie hij verwijst.
          const [prevId, currId] = [prev, curr].map((k) => k.verwijstNaar ?? k.person.id);
          const prevIsSpouseOfCurr = (relIndex.spousal.get(prevId) ?? []).some(
            (r) => (r.personA === prevId ? r.personB : r.personA) === currId
          );
          // #1089 — flag AAN: houd co-spouses (twee partners van DEZELFDE anker-
          // persoon) óók tight (spouseGap i.p.v. hGap), zodat een multi-partner-groep
          // niet uit elkaar wordt geschoven — consistent met de PEDIGREE-fix.
          // Bewust BEPERKT tot DESCENDANTS (waar layoutDescendants de voetafdruk
          // reserveert); PATRILINEAL/MATRILINEAL blijven onaangeroerd, ook met flag aan.
          let shareCommonSpouse = false;
          // #1599 — ook met partnerGap: een gap kleiner dan hGap duwde anders de 2e partner weg.
          // #1793 — en zonder voetafdruk-flag: twee of meer partners krijgen sindsdien altijd
          // hun volle voetafdruk (layoutDescendants, partnerPlan).
          if (viewMode === 'DESCENDANTS' && !prevIsSpouseOfCurr) {
            const prevPartners = new Set(
              (relIndex.spousal.get(prevId) ?? []).map((r) =>
                r.personA === prevId ? r.personB : r.personA
              )
            );
            shareCommonSpouse = (relIndex.spousal.get(currId) ?? []).some((r) =>
              prevPartners.has(r.personA === currId ? r.personB : r.personA)
            );
          }
          // #1089 — twee "missing parent" placeholders van DEZELFDE kind vormen
          // het ghost-koppel (vader links, moeder rechts). generateParentPlaceholders
          // plaatst ze symmetrisch op kind.x ± d/2 (d = breedte + spouseGap), dus
          // precies één spouseGap uit elkaar. Zonder couple-erkenning ziet deze pass
          // ze als siblings en duwt de moeder met hGap weg → asymmetrisch koppel.
          // Behandel het paar daarom als koppel (spouseGap), net als een echt paar,
          // zodat het symmetrisch onder het kind blijft. Verschillende kinderen (bv.
          // moeder-placeholder van vader naast vader-placeholder van moeder) blijven
          // siblings → hGap, zodat no-overlap intact blijft.
          // #1957 — een archiefvoorstel telt hier als placeholder (ook gemengd).
          const arePlaceholderCouple =
            isSpookknoop(prev) &&
            isSpookknoop(curr) &&
            prev.placeholderForChildId !== undefined &&
            prev.placeholderForChildId === curr.placeholderForChildId;
          const areCouplePost = prevIsSpouseOfCurr || shareCommonSpouse || arePlaceholderCouple;
          const postDims = getNodeDims(Math.abs(prev.generation));
          const minGap = areCouplePost ? (descPartnerGap ?? postDims.spouseGap) : postDims.hGap;
          const minX = prev.x + prevHalfW + currHalfW + minGap;
          if (curr.x < minX) {
            const shift = minX - curr.x;

            // Shift everything from index i rightward.
            for (let j = i; j < rowNodes.length; j++) {
              rowNodes[j].x += shift;
            }

            // Update links for all shifted nodes
            for (const link of allLinks) {
              const srcNode = nodeById.get(link.sourceId);
              const tgtNode = nodeById.get(link.targetId);
              if (srcNode) link.sourceX = srcNode.x;
              if (tgtNode) link.targetX = tgtNode.x;
            }

            changed = true;
            rowNodes.sort((a, b) => a.x - b.x); // re-sort after shifts
            break; // restart scan
          }
        }
      }
    }
  }

  // #1590 — gezinslijnen pas nu op de definitieve posities (na partners + overlap).
  plaatsGezinslijnen(allNodes, allLinks);

  // #1089 — record the post-packing position of any placeholder that was nudged
  // off its ideal Sosa slot (couple edge: "spouse wijkt, directe lijn niet").
  // `placeholderX` carries the adjusted x; `x` stays in sync (= rendered pos).
  for (const ph of spookknopen) {
    const ideal = placeholderIdealX.get(ph.person.id);
    if (ideal !== undefined && Math.abs(ph.x - ideal) > 0.001) {
      ph.placeholderX = ph.x;
    }
  }

  // Post-process: detect hidden parents/children for all nodes
  const allNodeIds = new Set(allNodes.map((n) => n.person.id));
  for (const node of allNodes) {
    // Hidden parents: person has parents in dataset but none are visible nodes
    if (node.hasHiddenParents === undefined) {
      const parentRels = relIndex.byChild.get(node.person.id) ?? [];
      const visibleParents = parentRels.filter((r) => allNodeIds.has(r.personA));
      node.hasHiddenParents =
        hasParentsInDataset(node.person.id, relIndex, personMap) && visibleParents.length === 0;
    }
    // Hidden children: person has children in dataset but none are visible nodes
    if (node.hasHiddenChildren === undefined) {
      const childRels = relIndex.byParent.get(node.person.id) ?? [];
      const visibleChildren = childRels.filter((r) => allNodeIds.has(r.personB));
      const totalChildren = childRels.filter((r) => personMap.has(r.personB)).length;
      node.hasHiddenChildren = totalChildren > 0 && visibleChildren.length < totalChildren;
      node.hiddenChildCount = totalChildren - visibleChildren.length;
    }
  }
  // #1934 (NC #877, Twan 29-10) — kinderen die niet in beeld staan horen bij het paar: hun gezamenlijke
  // kinderen staan één keer op de lijn tussen vader en moeder. Bij een ouder blijven alleen de kinderen
  // die hij met geen partner in beeld deelt (uit een ander huwelijk).
  const echteId = (id: string) => (id.startsWith('__verwijs__') ? id.split('__')[3] : id);
  const kinderenVan = (id: string) =>
    (relIndex.byParent.get(id) ?? []).filter((r) => personMap.has(r.personB)).map((r) => r.personB);
  const buitenBeeld = (id: string) => new Set(kinderenVan(id).filter((k) => !allNodeIds.has(k)));
  const gedeeld = new Map<string, Set<string>>();
  for (const link of allLinks) {
    if (link.type !== 'spousal') continue;
    const a = echteId(link.sourceId);
    const b = echteId(link.targetId);
    const vanB = buitenBeeld(b);
    const samen = [...buitenBeeld(a)].filter((k) => vanB.has(k));
    link.hiddenChildCount = samen.length || undefined;
    // #1954 (NC #886) — de pil telt ze allemaal ("7 kinderen"), niet alleen wie niet in beeld staat.
    if (samen.length) {
      const vanBAlle = new Set(kinderenVan(b));
      link.sharedChildCount = new Set(kinderenVan(a).filter((k) => vanBAlle.has(k))).size;
    }
    for (const id of [a, b]) {
      const s = gedeeld.get(id) ?? new Set<string>();
      samen.forEach((k) => s.add(k));
      gedeeld.set(id, s);
    }
  }
  for (const node of allNodes) {
    if (!node.hiddenChildCount) continue;
    const al = gedeeld.get(echteId(node.person.id));
    if (al)
      node.hiddenChildCount = [...buitenBeeld(echteId(node.person.id))].filter(
        (k) => !al.has(k)
      ).length;
  }

  // Center everything so the visible root is at (0, 0). Voor DESCENDANTS = de
  // gekozen focus-persoon; voor overige views eveneens focusPersonId.
  centerOnProband(allNodes, allLinks, focusPersonId);

  // #525 — Wijs een card-density niveau toe op basis van afstand tot proband.
  // Deze waarde wordt later door treePdfExport.buildCardSvg gebruikt om het
  // juiste D4-rendering pad te kiezen (detailed/standard/compact).
  for (const node of allNodes) {
    node.cardLevel = assignCardLevel(node.generation);
  }

  // Plan D — D'Aboville-nummering + spouse-inline annotaties voor DESCENDANTS.
  // Alleen actief in DESCENDANTS-modus; raakt pedigree/lijn-views niet.
  if (viewMode === 'DESCENDANTS') {
    // Spouse-nodes worden buiten de nummering gehouden (isSpouseNode=true).
    const mainDescNodes = allNodes.filter((n) => !n.isSpouseNode);

    // Stap 1: bouw de D'Aboville-boom vanaf de FOCUS-persoon (= de wortel van de
    // getoonde nakomelingenstaat). De boom loopt over de volledige data, met
    // dezelfde kind-volgorde als de layout (getChildrenGroupedByPartner) zodat de
    // nummers consistent zijn met het beeld. Focus = 1, kinderen 1.1/1.2, enz.
    const buildFullDabovilleNode = (id: string, seen: Set<string>): DabovilleNode => {
      seen.add(id);
      const childIds = getChildrenGroupedByPartner(id, relIndex, personMap, events).flatMap((g) =>
        g.children.map((c) => c.id)
      );
      return {
        id,
        children: childIds
          .filter((cId) => personMap.has(cId) && !seen.has(cId) && !isNakomKindId(cId))
          .map((cId) => buildFullDabovilleNode(cId, seen)),
      };
    };

    const dabovilleMap = assignDabovilleNumbers(
      buildFullDabovilleNode(focusPersonId, new Set<string>())
    );

    // Stap 2: annoteer elke zichtbare main-node met zijn D'Aboville-nummer. #1808 — een
    // verwijskaart draagt het nummer van de echte kaart (export: "zie 1.2.2").
    for (const node of allNodes) {
      if (node.isSpouseNode && !node.verwijstNaar) continue;
      const num = dabovilleMap.get(node.verwijstNaar ?? node.person.id);
      if (num !== undefined) {
        (node as LayoutNode & { __daboville?: string }).__daboville = num;
      }
    }

    // Stap 3: voor elke kind-descendant (niet de proband zelf) die een
    // echtgeno(o)t(e) heeft, koppel de eerste echtgeno(o)t(e) als
    // spouse-inline data. Gebruik spousal links om marriage-datum + -plaats
    // op te halen (spousal links bevatten marriageDate + marriagePlace uit §5).
    // B-19 — indexeer op het PAAR, niet op één persoon.
    //
    // Hier werd per persoon de EERSTE spousal-link bewaard die langskwam, maar de
    // partner die daarna getoond wordt is `node.spouses[0]`. Bij iemand met
    // meerdere partners hoeft dat niet dezelfde te zijn: de linkvolgorde hangt af
    // van welke node eerder in attachSpouses verwerkt werd. Resultaat:
    // "Jan Jansen × Maria de Wit, geh. 1895" waarbij 1895 het huwelijk met Anna
    // was. Een verkeerde datum bij de juiste partner is erger dan geen datum.
    const paarSleutel = (a: string, b: string) => [a, b].sort().join('|');
    const spousalLinkByPair = new Map<string, LayoutLink>();
    const echtId = new Map(
      allNodes.filter((k) => k.verwijstNaar).map((k) => [k.person.id, k.verwijstNaar!])
    );
    const echt = (id: string) => echtId.get(id) ?? id;
    for (const link of allLinks) {
      if (link.type === 'spousal') {
        const sleutel = paarSleutel(echt(link.sourceId), echt(link.targetId));
        if (!spousalLinkByPair.has(sleutel)) spousalLinkByPair.set(sleutel, link);
      }
    }

    for (const node of mainDescNodes) {
      if (node.person.id === focusPersonId) continue; // proband krijgt eigen couple-rij
      if (!node.spouses || node.spouses.length === 0) continue; // geen partner → geen inline

      const firstSpouse = node.spouses[0];
      if (!firstSpouse) continue;

      const spousalLink = spousalLinkByPair.get(paarSleutel(node.person.id, firstSpouse.id));
      const marriageDate = spousalLink?.marriageDate;
      const marriagePlace = spousalLink?.marriagePlace;

      (
        node as LayoutNode & {
          __descendantSpouse?: {
            spousePerson: Person;
            marriageDate?: string;
            marriagePlace?: string;
          };
        }
      ).__descendantSpouse = {
        spousePerson: firstSpouse,
        marriageDate,
        marriagePlace,
      };
    }
  }

  return {
    nodes: allNodes,
    links: allLinks,
    dataMaxGeneration,
  };
}

/**
 * Backwards-compat alias: re-export onder de oude `computeTreeLayoutPure` naam
 * voor callers die de naam zonder versie-suffix gebruiken. Identiek aan
 * `computeTreeLayoutPureV1`.
 */
export const computeTreeLayoutPure = computeTreeLayoutPureV1;

/**
 * Legacy V1 hook (memo-wrapper rond `computeTreeLayoutPureV1`).
 *
 * Behoud van de oorspronkelijke `useTreeLayout`-implementatie als losse export
 * zodat tests, debug-tools, en de orchestrator deze expliciet kunnen aanroepen.
 */
/**
 * #1590 — de layout zoals de Stamboom-pagina hem tekent (V1 + de vlaggen van het
 * scherm), zonder React. Gebruikt door `useTreeLayoutV1`.
 */
export function computeScreenTreeLayout(
  people: Person[],
  relationships: Relationship[],
  focusPersonId: string,
  viewMode: TreeViewMode,
  depthLimit: number,
  isEditMode: boolean = false,
  rootId?: string,
  showSpouses: boolean = true,
  collapsedPartnerNodes?: Set<string>,
  descendantDepth: number = 0,
  events: GenealogyEvent[] = [],
  voorstelPlekken?: LaagPlek[]
): { nodes: LayoutNode[]; links: LayoutLink[]; dataMaxGeneration: number } {
  const partnerSpacing = getFeatureFlag<string>('tree.partnerSpacing', 'off') === 'on';
  const base = computeTreeLayoutPureV1(
    people,
    relationships,
    focusPersonId,
    viewMode,
    depthLimit,
    isEditMode,
    rootId,
    showSpouses,
    collapsedPartnerNodes,
    descendantDepth,
    events,
    partnerSpacing,
    getFeatureFlag<string>('tree.relationTypeLines', 'off') === 'on',
    // #1089 — placeholders alleen wanneer de flag aan staat ÉN edit-modus actief is.
    getFeatureFlag<string>('tree.editPlaceholders', 'off') === 'on' && isEditMode,
    getFeatureFlag<string>('tree.implex', 'off') === 'on' && viewMode === 'PEDIGREE',
    undefined, // partnerGap: alleen de rapportplaten
    SCHERM_WISSEL_EXTRA, // #1606 ronde 2 — knop en "(2)" vrij tussen vrouw en 2e man
    voorstelPlekken
  );
  // #586/#562/#1039 — opt-in voetafdruk-bewuste partner-spacing op PEDIGREE.
  // Clone vóór muteren (zoals prepareExportNodes) zodat de memo-cache intact blijft.
  if (partnerSpacing && viewMode === 'PEDIGREE') {
    const nodes = base.nodes.map((n) => ({ ...n }));
    const links = base.links.map((l) => ({ ...l }));
    applyScreenPartnerSpacing({ nodes, links });
    return { ...base, nodes, links };
  }
  return base;
}

export function useTreeLayoutV1(
  people: Person[],
  relationships: Relationship[],
  focusPersonId: string,
  viewMode: TreeViewMode,
  depthLimit: number,
  isEditMode: boolean = false,
  rootId?: string,
  showSpouses: boolean = true,
  collapsedPartnerNodes?: Set<string>,
  descendantDepth: number = 0,
  events: GenealogyEvent[] = [],
  voorstelPlekken?: LaagPlek[]
): {
  nodes: LayoutNode[];
  links: LayoutLink[];
  dataMaxGeneration: number;
} {
  const partnerSpacing: string = getFeatureFlag<string>('tree.partnerSpacing', 'off');
  const relationLinesFlag: string = getFeatureFlag<string>('tree.relationTypeLines', 'off');
  // #1089 — placeholders alleen wanneer de flag aan staat ÉN edit-modus actief is.
  const editPlaceholdersFlag: string = getFeatureFlag<string>('tree.editPlaceholders', 'off');
  const editPlaceholdersOn = editPlaceholdersFlag === 'on' && isEditMode;
  const implexFlag: string = getFeatureFlag<string>('tree.implex', 'off');
  const implexOn = implexFlag === 'on' && viewMode === 'PEDIGREE';
  return useMemo(
    () =>
      computeScreenTreeLayout(
        people,
        relationships,
        focusPersonId,
        viewMode,
        depthLimit,
        isEditMode,
        rootId,
        showSpouses,
        collapsedPartnerNodes,
        descendantDepth,
        events,
        voorstelPlekken
      ),
    // De vlaggen staan in de deps: computeScreenTreeLayout leest ze zelf.
    [
      people,
      relationships,
      focusPersonId,
      viewMode,
      depthLimit,
      isEditMode,
      rootId,
      showSpouses,
      collapsedPartnerNodes,
      descendantDepth,
      events,
      partnerSpacing,
      relationLinesFlag,
      editPlaceholdersOn,
      implexOn,
      voorstelPlekken,
    ]
  );
}

/**
 * Orchestrator: kiest tussen V1 (legacy, default) en V2 (variable-size aware)
 * op basis van de feature-flag `export.pipeline`. Signature identiek aan V1
 * zodat callers ongewijzigd blijven.
 *
 * **KRITIEK**: default-flag is `legacy`, dus geen visuele wijzigingen tenzij
 * de gebruiker expliciet `setFeatureFlag('export.pipeline', 'v2')` heeft
 * uitgevoerd in localStorage.
 *
 * V1 en V2 worden allebei altijd als hook AANGEROEPEN om React's "rules of
 * hooks" te respecteren (consistente call-order), maar V2 krijgt de flag als
 * `enabled` mee en doet geen werk zolang die op `legacy` staat. Eerder werd V2
 * onvoorwaardelijk doorgerekend en het resultaat weggegooid — dat kostte 2x de
 * layout-allocatie (gemeten 30,3 vs 16,2 MB bij 2000 nodes) en liet bovendien
 * `assertMaxNodes(people.length)` op het scherm-pad throwen bij projecten met
 * meer dan 2048 personen, wat de hele stamboompagina wit maakte.
 */
export function useTreeLayout(
  people: Person[],
  relationships: Relationship[],
  focusPersonId: string,
  viewMode: TreeViewMode,
  depthLimit: number,
  isEditMode: boolean = false,
  rootId?: string,
  showSpouses: boolean = true,
  collapsedPartnerNodes?: Set<string>,
  descendantDepth: number = 0,
  events: GenealogyEvent[] = [],
  voorstelPlekken?: LaagPlek[]
): {
  nodes: LayoutNode[];
  links: LayoutLink[];
  dataMaxGeneration: number;
} {
  // We resolven de flag op elke render — goedkoop genoeg en garandeert dat
  // toggle via DevTools direct effect heeft op de volgende render. Cast naar
  // wider type zodat TS de `=== 'v2'` vergelijking accepteert.
  const pipeline: string = getFeatureFlag<string>('export.pipeline', 'legacy');

  // BEIDE hooks MOETEN altijd worden aangeroepen voor stabiele hook-call-order
  // (rules of hooks) — maar V2 rekent alleen wanneer de flag op `v2` staat.
  const v1 = useTreeLayoutV1(
    people,
    relationships,
    focusPersonId,
    viewMode,
    depthLimit,
    isEditMode,
    rootId,
    showSpouses,
    collapsedPartnerNodes,
    descendantDepth,
    events,
    voorstelPlekken // #1957 — alleen het scherm (V1); V2 is export
  );
  const v2 = useTreeLayoutV2(
    people,
    relationships,
    focusPersonId,
    viewMode,
    depthLimit,
    isEditMode,
    rootId,
    showSpouses,
    collapsedPartnerNodes,
    descendantDepth,
    events,
    pipeline === 'v2'
  );

  return pipeline === 'v2' ? v2 : v1;
}

export default useTreeLayout;
